'use client';

import { c as _c } from "react/compiler-runtime";
import type { LivelinePoint } from '@morfi/chart';
import { useConvictionOpeningSnapshot } from '@/lib/chart/use-conviction-opening-snapshot';

// ⚠️ SENSITIVE FILE — Chart rendering & real-time data pipeline
// Changes here directly affect what users see on market charts (consensus, live prices, candles).
// If using AI assistance, MANUALLY TEST all chart variants before committing:
//   - Consensus mode (market share %)
//   - Live prices mode (line + candle)
//   - Pre-trade (new market, no data)
//   - During confirmation (resolving banner + pulsing line)
//   - Post-resolution (winner continues, loser clamped to 0)
//   - Mode switching (consensus ↔ live prices without "No data" flash)
//   - Timeframe switching (Live, 1H, 6H, 1D, 1W, 1M, All — each a lookback
//     window with an auto-derived bucket; never renders empty data)

import { type AuthoritativeConvictionChartPresentationSource, buildSparseConvictionChart, type ConvictionChartSnapshotInput, type ConvictionMarketFields, consensusThresholdSeries, convictionChartGeometryRevision, convictionChartTerminalTo, deriveMarketResolutionThresholdPolicy, earliestChangedConvictionChartTime, type GetMarketChartRow, type GetMarketRow, type MarketChartSnapshot, outcomeConvictionFraction, resolveConvictionChartStart, selectConvictionChartInterval, useActiveWallet, useAuth, useAuthoritativeConvictionChart, useGetMarket, useGetMarketBoostEvents, useGetMarketChart, useGetMarketField, useMarketConfig, useQueryClient, useServerClock } from '@morfi/backend-sdk-v2/client';
import { priceChartTargetBars, visibleBarEstimate } from '@morfi/chart-policy';
import { bondingCurveMarketCapFromQuoteReserveD6 } from '@morfi/market-math';
import { domAnimation, LazyMotion, m as motionElement, useReducedMotion } from 'motion/react';
import { useTranslations } from 'next-intl';
import { type Dispatch, type SetStateAction, startTransition, useDeferredValue, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { useAuthoritativeMarketSharePresentation, usePaintedMarketShareDisplayStore } from '@/components/chart/painted-market-share-display';
import { averageEntryPriceD9 } from '@/components/market/position-realisable';
import { TooltipProvider } from '@/components/ui/app-tooltip';
import { CHART_CANVAS_HEIGHT_DESKTOP, CHART_CANVAS_HEIGHT_MOBILE } from '@/config/chart';
import { D6_SCALE, D9_SCALE } from '@/config/solana';
import { useMarketTradeStreams } from '@/hooks/chart/use-market-trade-streams';
import { useOutcomeColor } from '@/hooks/chart/use-outcome-color';
import { useRetainedChartHistory, useRetainedChartSeeds } from '@/hooks/chart/use-retained-chart-history';
import { useTimeframeChartPresentation } from '@/hooks/chart/use-timeframe-chart-presentation';
import { useHeldWhile } from '@/hooks/market/use-held-while';
import { useMarketPositions, useOwnedMarketPositionScope } from '@/hooks/positions/use-market-positions';
import { useHydrated } from '@/hooks/ui/use-hydrated';
import { selectActiveTradeRows } from '@/lib/chart/active-trade-rows';
import { mapBoostEventRows } from '@/lib/chart/boost-events';
import { candleChartTargetBars } from '@/lib/chart/candle-chart-density';
import { chartAnchorFollowsRealtime, exclusiveChartRequestEnd, getAvailableIntervals, LIVE_CHART_SEED_SECONDS, LIVE_WINDOW_SECONDS, resolveAvailableInterval, windowSecondsForTimeframe } from '@/lib/chart/chart-factory';
import { publishChartLeafData } from '@/lib/chart/chart-leaf-publication';
import { initialMarketShare, resolveChartRequestStart } from '@/lib/chart/chart-window-boundaries';
import { resolveConnectedChartFramePolicy } from '@/lib/chart/connected-chart-frame-policy';
import { stopConsensusChartAtThreshold } from '@/lib/chart/consensus-stop';
import { isoToUnix } from '@/lib/chart/iso-time';
import { buildLiveMarketCapTrajectorySeries } from '@/lib/chart/live-market-cap-series';
import { liveTradePagesNeedMore } from '@/lib/chart/live-trade-pages';
import { buildLiveTradeCandles, buildLiveTradeSeries, LIVE_CANDLE_BUCKET_SECONDS } from '@/lib/chart/live-trade-series';
import { mapMarketChartRows } from '@/lib/chart/map-market-chart-rows';
import { type MarketChartOpening, readConvictionChartOpening, readMarketChartOpening } from '@/lib/chart/market-chart-opening';
import { type AuthoritativeTerminalPaintAck, CONSENSUS_THRESHOLD_INTERPOLATION, createMarketShareLivelineFrameSource, type MarketShareLivelineFrameSource } from '@/lib/chart/market-share-liveline-frame-source';
import { type LifecycleHandoff, type MetricChartTransition, resolveMetricChartTransition } from '@/lib/chart/metric-chart-transition';
import { outcomeHueToStepVar } from '@/lib/chart/outcome-hue';
import type { PaintedMarketShareDisplayStore, PaintedMarketShareSessionIdentity } from '@/lib/chart/painted-market-share-display';
import { toBackendChartType, toBackendProperty } from '@/lib/chart/resolve-chart-prefetch-args';
import { type CurrentChartLiquidityModel, resolveCurrentChartLiquidityModel } from '@/lib/chart/resolve-current-liquidity-model';
import { breakEvenMarketCapD6, currentCpMarketCapD6, currentOutcomeSpotChartValue, d6ToNum, mapOutcomeToChartOutcome, mapTradeToAnnotation, parseTradeLiquidityModel, pickChartLeafConverter } from '@/lib/chart/sdk-mapping';
import { clipTerminalChartDataAt, mergeTerminalChartOpeningPoints, resolveTerminalChartLifecycle, resolveTerminalChartOpeningEndUnix, resolveTerminalChartSnapKey, resolveTerminalChartWindowSecs } from '@/lib/chart/terminal-chart-lifecycle';
import { resolveTerminalConsensusSnapshot } from '@/lib/chart/terminal-consensus-snapshot';
import { createTimeframeRenderedFrameStore, isTimeframeViewportTransitionComplete, rememberCommittedTimeframeRenderedFrame, resolveTimeframeRenderedFrame, type TimeframeRenderedFrame, type TimeframeRenderedFrameStore } from '@/lib/chart/timeframe-rendered-frame';
import { resolveTradeSide } from '@/lib/chart/trade-side';
import type { BoostEvent, ChartInterval, ChartMode, ConcreteChartInterval, TradeAnnotation, YAxisProperty } from '@/lib/chart/types';
import { useAuthoritativeTerminalHandoff } from '@/lib/chart/use-authoritative-terminal-handoff';
import { useFormattedCurrency } from '@/lib/currency';
import { selectMarketDetailOutcome } from '@/lib/market/select-market-detail-outcome';
import { useActiveNetwork } from '@/lib/network/use-active-network';
import { logger } from '@/lib/telemetry/logger';
import { useOnchainTradeData } from '@/queries/onchain-trade-data';
import { useChartIntentOpening } from '@/stores/chart-intent';
import { useChartSettingsStore } from '@/stores/chart-settings';
import { ChartControls } from './chart-controls';
import { ChartSettingsMenu } from './chart-settings-menu';
import { type LiveMarketCapTrajectories, LiveMarketCapTrajectoryBoundary } from './live-market-cap-trajectory-boundary';
import { MarketChart, type MarketChartBoost, type MarketChartData, type MarketChartOutcome, type MarketChartTrade, type MarketChartTradeTapeEvent } from './market-chart';
import { useChartMarketSnapshot } from './use-chart-market-snapshot';

/** Before market age is known, expose only the always-available timeframes. */
/** Stable empty index array — avoids a new-array reference per render in the
 *  zustand selector (which would defeat re-render isolation). */
const EMPTY_INDICES: number[] = [];
const DEFAULT_AVAILABLE_INTERVALS: ReadonlySet<ChartInterval> = new Set<ChartInterval>(['live', 'all']);

/** Dim the retained snapshot while its replacement timeframe is loading. */
const TIMEFRAME_TARGET_LOADING_SERIES_OPACITY = 0.5;
const METRIC_CROSSFADE_SECONDS = 0.4;
const MARKET_CHART_DEGEN_OPTIONS = {
  suppressInitialBurst: true
} as const;
const EMPTY_CHART_DATA: Record<number, MarketChartData> = {};
const EMPTY_CHART_ROWS: readonly GetMarketChartRow[] = [];
const EMPTY_LIVE_TRAJECTORIES: LiveMarketCapTrajectories = {
  marketCap: {}
};

// ---------------------------------------------------------------------------
// Props
// ---------------------------------------------------------------------------

interface ConnectedChartContainerProps {
  readonly opening?: MarketChartOpening | undefined;
  marketPubkey: string;
  /** Exact SSR/client lower bound for the shared live trade-tape cache key. */
  tradesFrom: string;
  /** Verbose chart-debug logging — gated by the chart settings page. */
  debugMode?: boolean;
  className?: string;
  /**
   * When set, every series line is drawn in this single CSS-var color instead
   * of the per-outcome hue palette. The `/winners` token page passes
   * `'var(--foreground-brand)'` so all lines share one brand color (MOR-509).
   */
  seriesColorVar?: string;
  /**
   * Read-only history chart: hide the toolbar and force consensus mode over the
   * full market life (`all` interval). Does NOT write the global chart-settings
   * store. Default `false`.
   */
  readOnly?: boolean;
  /**
   * Override the chart canvas pixel height (default 420).
   */
  chartHeight?: number;
  /**
   * Override the reduced chart height applied below the `sm` breakpoint
   * (default 280).
   */
  mobileChartHeight?: number;
  /**
   * Render the live (60s) price line as a step-after staircase — the honest
   * shape of a trade-triggered AMM price (flat between an outcome's trades, a
   * vertical step on each). Default `true`. Pass `false` to fall back to the
   * smooth monotone (Fritsch–Carlson) spline, which interpolates a curve
   * between trades. Only affects the live price chart; other modes/intervals
   * are unchanged.
   */
  liveStepLine?: boolean;
  /**
   * Market status the page presents, when it differs from the cached row: the
   * market detail page holds `trading` while a resolution lands so the chart
   * locks together with the rest of the page.
   */
  lifecycleStatus?: string;
  /**
   * The page is holding a landed resolution. The chart stops projecting live
   * rows for as long as the page holds, including through a stale `trading`
   * refetch that the page's latch rides out.
   */
  resolutionHolding?: boolean;
  /** Effective config-driven resolution threshold, supplied by market detail's sole clock owner. */
  resolutionThreshold?: number;
}
interface ConnectedChartViewProps extends ConnectedChartContainerProps {
  readonly marketData: GetMarketRow | undefined;
  /** Static lifecycle fields bypass deferred rendering so terminal charts lock before paint. */
  readonly lifecycleStatus: string;
  readonly lifecycleCreatedAt: string;
  readonly lifecycleTradingStartedAt: string | null;
  readonly lifecycleResolvedAt: string | null;
  /** See {@link ConnectedChartContainerProps.resolutionHolding}. */
  readonly resolutionLanded: boolean;
}

// ---------------------------------------------------------------------------
// Helpers — value conversion
// ---------------------------------------------------------------------------
// Scale converters + SDK → chart mappers live in `@/lib/chart/sdk-mapping`
// so they can be unit-tested without an SDK provider. See that file for the
// D6/D9/decimal scale contract.

interface AutoPageTradeQuery {
  readonly hasNextPage: boolean;
  readonly isFetchingNextPage: boolean;
  readonly fetchNextPage: () => Promise<void>;
}
function useAutoPageTrades(query, enabled, stream, marketPubkey) {
  const $ = _c(12);
  let t0;
  if ($[0] !== enabled || $[1] !== marketPubkey || $[2] !== query || $[3] !== stream) {
    t0 = () => {
      if (!enabled || !query.hasNextPage || query.isFetchingNextPage) {
        return;
      }
      query.fetchNextPage().catch(error => {
        logger.warn("chart.trade_page_fetch_failed", {
          stream,
          marketPubkey,
          error: error instanceof Error ? error.message : String(error)
        });
      });
    };
    $[0] = enabled;
    $[1] = marketPubkey;
    $[2] = query;
    $[3] = stream;
    $[4] = t0;
  } else {
    t0 = $[4];
  }
  let t1;
  if ($[5] !== enabled || $[6] !== marketPubkey || $[7] !== query.fetchNextPage || $[8] !== query.hasNextPage || $[9] !== query.isFetchingNextPage || $[10] !== stream) {
    t1 = [enabled, marketPubkey, query.fetchNextPage, query.hasNextPage, query.isFetchingNextPage, stream];
    $[5] = enabled;
    $[6] = marketPubkey;
    $[7] = query.fetchNextPage;
    $[8] = query.hasNextPage;
    $[9] = query.isFetchingNextPage;
    $[10] = stream;
    $[11] = t1;
  } else {
    t1 = $[11];
  }
  useEffect(t0, t1);
}

/**
 * Module-level memo for the `available-intervals` Set. Keyed by
 * `(createdAt, ageMinutes)` so the Set identity is stable across renders
 * within the same minute. Without this, the IIFE in the component body
 * would return `new Set(...)` every render, and any downstream
 * `useEffect` that lists the Set in its deps would re-fire and trigger
 * a render loop (the React Compiler memoises this in prod, but it is
 * disabled in `pnpm dev` per `frontend/performance.md`). Pattern mirrors
 * `useOutcomeColor`'s stable-memo cache.
 */
const _intervalSetCache: Map<string, ReadonlySet<ChartInterval>> = new Map<string, ReadonlySet<ChartInterval>>();
const _outcomeIndicesCache: Map<string, readonly number[]> = new Map<string, readonly number[]>();

/** Stable exact outcome fan-out for the generic market chart. */
function getRenderedOutcomeIndices(outcomeCount: number): readonly number[] {
  const key = String(outcomeCount);
  const cached = _outcomeIndicesCache.get(key);
  if (cached !== undefined) return cached;
  const indices = Array.from({
    length: outcomeCount
  }, (_, index) => index);
  if (_outcomeIndicesCache.size > 64) _outcomeIndicesCache.clear();
  _outcomeIndicesCache.set(key, indices);
  return indices;
}
function getStableAvailableIntervals(marketCreatedAtRaw: number | undefined, marketCreatedAt: number, lifespanSeconds: number): ReadonlySet<ChartInterval> | undefined {
  if (marketCreatedAtRaw === undefined) return undefined;
  // 1-minute bucket — coarser if we ever need it. For a resolved market the
  // lifespan is fixed (creation→resolution), so the key is stable; for a live
  // market it grows a minute at a time.
  const lifespanMin = Math.floor(lifespanSeconds / 60);
  const key = `${marketCreatedAt}|${lifespanMin}`;
  const cached = _intervalSetCache.get(key);
  if (cached !== undefined) return cached;
  // `live` and `all` are always present; windowed timeframes appear only when
  // their lookback fits inside the market's lifetime (never-empty guarantee).
  const result = getAvailableIntervals(lifespanSeconds);
  // Soft cap to bound memory; full clear on overflow.
  if (_intervalSetCache.size > 256) _intervalSetCache.clear();
  _intervalSetCache.set(key, result);
  return result;
}
function earliestChartPointUnix(chartData: Readonly<Record<number, MarketChartData>>): number | undefined {
  let earliest: number | undefined;
  for (const entry of Object.values(chartData)) {
    for (const point of entry.line) {
      if (!Number.isFinite(point.time) || point.time <= 0) continue;
      earliest = earliest === undefined ? point.time : Math.min(earliest, point.time);
    }
  }
  return earliest;
}

// ---------------------------------------------------------------------------
// Per-outcome chart leaf — one v2 `useGetMarketChart` call per slot.
// Slots beyond the market's outcome count short-circuit (no fetch, no NATS sub).
// Returns `{ line, ohlcv? }` already converted to display numbers, ready to
// drop into the `<MarketChart>` `chartData` map.
// ---------------------------------------------------------------------------

interface ChartLeafResult {
  /** Query identity used to fence stale interval responses from the active chart. */
  readonly requestKey?: string;
  readonly line: ReadonlyArray<{
    time: number;
    value: number;
  }>;
  readonly lineTimeKind?: 'sample';
  /** Exact value in effect immediately before this sparse response window. */
  readonly leftEdgeValue?: number;
  readonly ohlcv?: ReadonlyArray<{
    time: number;
    open: number;
    high: number;
    low: number;
    close: number;
  }>;
  /** The per-outcome bonding-curve virtual quote reserve (D6 integer string),
   *  read off the `market_cap` line buckets. It's a STABLE curve constant used
   *  with the chain pool snapshot; the SDK's realtime diagnostic observes
   *  `effectiveMarketCapD6`, never the virtual quote reserve.
   *  alias. Only populated by the `market_cap` line leaf. */
  readonly virtualQuote?: string;
  /** Exact CAGG width selected by the backend for this response. */
  readonly bucketSeconds?: number;
}

/** Stable setter dispatchers from the parent's `useState`. useState returns
 *  identity-stable setters that never change, so passing them directly into
 *  the leaf avoids a handler-identity dep cycle that would re-fire the leaf's
 *  effect on every parent render (the React Compiler memoizes inline handlers
 *  in prod, but is OFF in `pnpm dev` per `frontend/performance.md` — without
 *  this, dev hits an infinite render loop). */
type SetData = (updater: (prev: Record<number, ChartLeafResult | undefined>) => Record<number, ChartLeafResult | undefined>) => void;
interface ChartLeafProps {
  readonly marketPubkey: string;
  readonly outcomeIndex: number;
  readonly interval: ConcreteChartInterval | 'all';
  readonly property: string;
  readonly chartType: 'line' | 'ohlcv';
  readonly from?: string;
  readonly to?: string;
  readonly liveWindowSeconds?: string;
  readonly targetBars?: number;
  /** Query identity for the active historical timeframe. */
  readonly requestKey?: string;
  /** Main timeframe leaves retain their last response while a sibling interval loads. */
  readonly retainDataOnUnmount?: boolean;
  /** Stable from `useState` — safe in useEffect deps. */
  readonly setData: SetData;
  /** Advances the parent query/viewport anchor only when this leaf receives data. */
  readonly setAnchorMinuteMs?: Dispatch<SetStateAction<number>>;
}
interface HistoricalChartPublisherConfig extends Pick<ChartLeafProps, 'requestKey' | 'retainDataOnUnmount' | 'setData' | 'setAnchorMinuteMs'> {}
interface HistoricalChartQueryOwnerProps extends Pick<ChartLeafProps, 'marketPubkey' | 'outcomeIndex' | 'interval' | 'property' | 'chartType' | 'from' | 'to' | 'liveWindowSeconds' | 'targetBars'> {
  readonly publisher?: HistoricalChartPublisherConfig;
}

/** Keeps a historical query and its realtime bucket owner mounted without
 * losing the exact cache identity returned by the SDK. The selected owner also
 * publishes its cache snapshot; retained sibling metrics only keep their query
 * and realtime bucket alive. */
function HistoricalChartQueryOwner(t0) {
  const $ = _c(32);
  const {
    marketPubkey,
    outcomeIndex,
    interval,
    property,
    chartType,
    from,
    to,
    liveWindowSeconds,
    targetBars,
    publisher
  } = t0;
  const t1 = String(outcomeIndex);
  let t2;
  if ($[0] !== from) {
    t2 = from !== undefined ? {
      from
    } : {};
    $[0] = from;
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  let t3;
  if ($[2] !== to) {
    t3 = to !== undefined ? {
      to
    } : {};
    $[2] = to;
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  let t4;
  if ($[4] !== liveWindowSeconds) {
    t4 = liveWindowSeconds === undefined ? {} : {
      liveWindowSeconds
    };
    $[4] = liveWindowSeconds;
    $[5] = t4;
  } else {
    t4 = $[5];
  }
  let t5;
  if ($[6] !== interval || $[7] !== targetBars) {
    t5 = interval !== "all" || targetBars === undefined ? {} : {
      targetBars
    };
    $[6] = interval;
    $[7] = targetBars;
    $[8] = t5;
  } else {
    t5 = $[8];
  }
  let t6;
  if ($[9] !== chartType || $[10] !== interval || $[11] !== marketPubkey || $[12] !== property || $[13] !== t1 || $[14] !== t2 || $[15] !== t3 || $[16] !== t4 || $[17] !== t5) {
    t6 = {
      pubkey: marketPubkey,
      outcomeIndex: t1,
      interval,
      property,
      chartType,
      ...t2,
      ...t3,
      ...t4,
      ...t5
    };
    $[9] = chartType;
    $[10] = interval;
    $[11] = marketPubkey;
    $[12] = property;
    $[13] = t1;
    $[14] = t2;
    $[15] = t3;
    $[16] = t4;
    $[17] = t5;
    $[18] = t6;
  } else {
    t6 = $[18];
  }
  const query = useGetMarketChart(t6);
  if (publisher === undefined) {
    return null;
  }
  let t7;
  if ($[19] !== liveWindowSeconds) {
    t7 = liveWindowSeconds === undefined ? {} : {
      liveWindowSeconds
    };
    $[19] = liveWindowSeconds;
    $[20] = t7;
  } else {
    t7 = $[20];
  }
  let t8;
  if ($[21] !== chartType || $[22] !== interval || $[23] !== marketPubkey || $[24] !== outcomeIndex || $[25] !== property || $[26] !== publisher || $[27] !== query.data || $[28] !== query.isError || $[29] !== query.isPending || $[30] !== t7) {
    t8 = <ChartLeafPublisher marketPubkey={marketPubkey} outcomeIndex={outcomeIndex} interval={interval} property={property} chartType={chartType} {...t7} {...publisher} envelope={query.data} isPending={query.isPending} isError={query.isError} />;
    $[21] = chartType;
    $[22] = interval;
    $[23] = marketPubkey;
    $[24] = outcomeIndex;
    $[25] = property;
    $[26] = publisher;
    $[27] = query.data;
    $[28] = query.isError;
    $[29] = query.isPending;
    $[30] = t7;
    $[31] = t8;
  } else {
    t8 = $[31];
  }
  return t8;
}
interface ChartLeafPublisherProps extends ChartLeafProps {
  readonly envelope: MarketChartSnapshot | undefined;
  readonly isPending: boolean;
  readonly isError: boolean;
}
function ChartLeafPublisher(t0) {
  const $ = _c(38);
  const {
    marketPubkey,
    outcomeIndex,
    interval,
    property,
    chartType,
    liveWindowSeconds,
    requestKey,
    retainDataOnUnmount: t1,
    setData,
    setAnchorMinuteMs,
    envelope,
    isPending,
    isError
  } = t0;
  const retainDataOnUnmount = t1 === undefined ? false : t1;
  let t2;
  let t3;
  if ($[0] !== outcomeIndex || $[1] !== retainDataOnUnmount || $[2] !== setData) {
    t2 = () => () => {
      if (retainDataOnUnmount) {
        return;
      }
      publishChartLeafData(setData, outcomeIndex, undefined);
    };
    t3 = [outcomeIndex, retainDataOnUnmount, setData];
    $[0] = outcomeIndex;
    $[1] = retainDataOnUnmount;
    $[2] = setData;
    $[3] = t2;
    $[4] = t3;
  } else {
    t2 = $[3];
    t3 = $[4];
  }
  useEffect(t2, t3);
  const rows = envelope?.data ?? EMPTY_CHART_ROWS;
  const warnedCandleContractFingerprint = useRef(null);
  const warnedQueryFailureFingerprint = useRef(null);
  let t4;
  if ($[5] !== chartType || $[6] !== envelope || $[7] !== interval || $[8] !== isError || $[9] !== isPending || $[10] !== liveWindowSeconds || $[11] !== marketPubkey || $[12] !== outcomeIndex || $[13] !== property || $[14] !== requestKey || $[15] !== rows || $[16] !== setAnchorMinuteMs || $[17] !== setData) {
    t4 = () => {
      if (isPending) {
        return;
      }
      if (isError) {
        const fingerprint = `${marketPubkey}|${interval}|${property}|${chartType}|${outcomeIndex}`;
        if (warnedQueryFailureFingerprint.current !== fingerprint) {
          warnedQueryFailureFingerprint.current = fingerprint;
          logger.warn("chart.history_load_failed", {
            marketPubkey,
            interval,
            metric: property,
            chartType,
            outcomeIndex
          });
        }
        publishChartLeafData(setData, outcomeIndex, {
          line: [],
          ...(requestKey !== undefined ? {
            requestKey
          } : {})
        });
        return;
      }
      warnedQueryFailureFingerprint.current = null;
      const convert = pickChartLeafConverter(property);
      const convertedLeftEdge = envelope?.leftEdgeValue === undefined ? undefined : convert(envelope.leftEdgeValue);
      const rollingFrom = liveWindowSeconds === undefined ? undefined : Math.floor(Date.now() / 1000) - Number(liveWindowSeconds);
      const predecessor = rollingFrom === undefined ? undefined : rows.findLast(row => Date.parse(row.bucket) / 1000 < rollingFrom);
      const predecessorValue = predecessor === undefined ? undefined : convert("value" in predecessor ? predecessor.value : predecessor.close);
      const mapped = mapMarketChartRows(rows, chartType, convert, {
        ...(envelope?.virtualQuote !== undefined ? {
          virtualQuote: envelope.virtualQuote
        } : {}),
        ...(convertedLeftEdge !== undefined && Number.isFinite(convertedLeftEdge) ? {
          leftEdgeValue: convertedLeftEdge
        } : {}),
        ...(envelope?.bucketSeconds !== undefined ? {
          bucketSeconds: envelope.bucketSeconds
        } : {}),
        ...(envelope?.rangeStart !== undefined ? {
          requestedFrom: envelope.rangeStart
        } : {}),
        ...(envelope?.rangeEnd !== undefined ? {
          requestedTo: envelope.rangeEnd
        } : {}),
        ...(envelope?.closedBefore !== undefined ? {
          closedBefore: envelope.closedBefore
        } : {}),
        ...(rollingFrom === undefined ? {} : {
          requestedFrom: new Date(rollingFrom * 1000).toISOString(),
          requestedTo: new Date().toISOString(),
          ...(predecessorValue === undefined ? {} : {
            leftEdgeValue: predecessorValue
          })
        })
      });
      const {
        candleContractViolation,
        ...chartEntry
      } = mapped;
      if (candleContractViolation === undefined) {
        warnedCandleContractFingerprint.current = null;
      } else {
        const fingerprint_0 = `${marketPubkey}|${interval}|${property}|${outcomeIndex}|${candleContractViolation.fingerprint}`;
        if (warnedCandleContractFingerprint.current !== fingerprint_0) {
          warnedCandleContractFingerprint.current = fingerprint_0;
          logger.warn("chart.candle_contract_invalid", {
            marketPubkey,
            interval,
            metric: property,
            outcomeIndex,
            violationCount: candleContractViolation.count,
            reasons: candleContractViolation.reasons
          });
        }
      }
      const nextEntry = {
        ...chartEntry,
        ...(requestKey !== undefined ? {
          requestKey
        } : {})
      };
      publishChartLeafData(setData, outcomeIndex, nextEntry);
      if (setAnchorMinuteMs !== undefined && chartAnchorFollowsRealtime(interval)) {
        setAnchorMinuteMs(_temp);
      }
    };
    $[5] = chartType;
    $[6] = envelope;
    $[7] = interval;
    $[8] = isError;
    $[9] = isPending;
    $[10] = liveWindowSeconds;
    $[11] = marketPubkey;
    $[12] = outcomeIndex;
    $[13] = property;
    $[14] = requestKey;
    $[15] = rows;
    $[16] = setAnchorMinuteMs;
    $[17] = setData;
    $[18] = t4;
  } else {
    t4 = $[18];
  }
  const t5 = envelope?.virtualQuote;
  const t6 = envelope?.leftEdgeValue;
  const t7 = envelope?.bucketSeconds;
  const t8 = envelope?.rangeStart;
  const t9 = envelope?.rangeEnd;
  const t10 = envelope?.closedBefore;
  let t11;
  if ($[19] !== chartType || $[20] !== interval || $[21] !== isError || $[22] !== isPending || $[23] !== liveWindowSeconds || $[24] !== marketPubkey || $[25] !== outcomeIndex || $[26] !== property || $[27] !== requestKey || $[28] !== rows || $[29] !== setAnchorMinuteMs || $[30] !== setData || $[31] !== t10 || $[32] !== t5 || $[33] !== t6 || $[34] !== t7 || $[35] !== t8 || $[36] !== t9) {
    t11 = [outcomeIndex, rows, isPending, isError, property, chartType, liveWindowSeconds, marketPubkey, t5, t6, t7, t8, t9, t10, setData, setAnchorMinuteMs, interval, requestKey];
    $[19] = chartType;
    $[20] = interval;
    $[21] = isError;
    $[22] = isPending;
    $[23] = liveWindowSeconds;
    $[24] = marketPubkey;
    $[25] = outcomeIndex;
    $[26] = property;
    $[27] = requestKey;
    $[28] = rows;
    $[29] = setAnchorMinuteMs;
    $[30] = setData;
    $[31] = t10;
    $[32] = t5;
    $[33] = t6;
    $[34] = t7;
    $[35] = t8;
    $[36] = t9;
    $[37] = t11;
  } else {
    t11 = $[37];
  }
  useEffect(t4, t11);
  return null;
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------
function _temp(previous) {
  const next = Math.floor(Date.now() / 60000) * 60000;
  return next > previous ? next : previous;
}
interface ConnectedChartRenderedFrame extends TimeframeRenderedFrame<Record<number, MarketChartData>> {
  readonly metricTransitionGroupKey: string;
  readonly lifecycleHandoff: LifecycleHandoff;
  readonly chartMode: ChartMode;
  readonly yAxisProperty: YAxisProperty;
  readonly liveValues: Record<number, number> | undefined;
  readonly authoritativeFrameSource: MarketShareLivelineFrameSource | undefined;
}
interface ConnectedChartModel {
  t: ReturnType<typeof useTranslations>;
  interval: ChartInterval;
  chartMode: ChartMode;
  yAxisProperty: YAxisProperty;
  renderedOutcomeIndices: readonly number[];
  backendProperty: string;
  backendChartType: 'line' | 'ohlcv';
  backendInterval: ConcreteChartInterval | 'all';
  chartFrom: string | undefined;
  chartTo: string | undefined;
  liveWindowSeconds: string | undefined;
  targetBars: number;
  marketShareSamples: number;
  openingConviction: ConvictionChartSnapshotInput | null;
  retireOpeningConviction: () => void;
  onPlotWidthChange: (width: number) => void;
  chartSeriesKey: string;
  chartMetricTransitionGroupKey: string;
  chartDataRequestKey: string;
  setChartLeafData: SetData;
  presentationHistoryRequestKey: string | undefined;
  setRetainedChartLeafData: SetData;
  terminalOpening: {
    readonly from: string;
    readonly to: string;
    readonly requestKey: string;
  } | undefined;
  setTerminalOpeningData: SetData;
  setNowMinuteMs: Dispatch<SetStateAction<number>>;
  marketCapLiveEnabled: boolean;
  setMarketCapSeedData: SetData;
  outcomeCount: number;
  liveChartReady: boolean;
  buildLiveChartData: (trajectories: LiveMarketCapTrajectories) => Record<number, MarketChartData>;
  renderChartData: Record<number, MarketChartData> | null;
  renderedChartFrameStore: TimeframeRenderedFrameStore<Record<number, MarketChartData>, ConnectedChartRenderedFrame>;
  timeframeTransitionFrame: TimeframeRenderedFrame<Record<number, MarketChartData>> | null;
  timeframeTransitionGeneration: number;
  targetChartReady: boolean;
  outcomes: ReadonlyArray<MarketChartOutcome> | undefined;
  breakevens: ReadonlyArray<{
    outcomeIndex: number;
    priceUsdc: number;
    marketCapUsdc?: number;
  }>;
  showBreakEven: boolean;
  v2Trades: ReadonlyArray<MarketChartTrade>;
  v2Boosts: ReadonlyArray<MarketChartBoost>;
  tradeTape: ReadonlyArray<MarketChartTradeTapeEvent>;
  hiddenOutcomes: ReadonlyArray<number>;
  v2Threshold: number | undefined;
  resolutionMarkerUnix: number | undefined;
  chartRightEdgeSec: number;
  candleNowSec: number;
  v2LiveValues: Record<number, number> | undefined;
  marketChartMode: 'line' | 'candle';
  focusedOutcomeIndex: number;
  candleWidthSeconds: number;
  requestedWindowSecs: number;
  effectiveWindowSecs: number;
  dataAvailableIntervals: ReadonlySet<ChartInterval> | undefined;
  locked: boolean;
  v2OutcomesWithColor: ReadonlyArray<{
    index: number;
    title: string;
    price: number;
    color: string;
  }>;
  setChartMode: (mode: ChartMode) => void;
  setChartInterval: (interval: ChartInterval) => void;
  setYAxisProperty: (property: YAxisProperty) => void;
  setSelectedOutcome: (marketPubkey: string, outcomeIndex: number) => void;
  toggleOutcome: (marketPubkey: string, outcomeIndex: number) => void;
}
type ConnectedOutcomeRow = GetMarketRow['outcomes'][number];
interface ConnectedOutcomeState {
  readonly rows: ReadonlyArray<ConnectedOutcomeRow>;
  readonly outcomeCount: number;
  readonly renderedOutcomeIndices: readonly number[];
  readonly winnerOutcomeIndex: number | null;
  readonly liquidityModelByOutcome: ReadonlyMap<number, CurrentChartLiquidityModel | null>;
  readonly indexedSupplyByOutcome: ReadonlyMap<number, string | null | undefined>;
  readonly currentCpMarketCapByOutcome: ReadonlyMap<number, bigint>;
  readonly unavailableCpMarketCapOutcomeIndicesKey: string;
}
function buildConnectedOutcomeState(marketData: GetMarketRow | undefined): ConnectedOutcomeState {
  const rows = marketData?.outcomes ?? [];
  const outcomeCount = rows.length;
  const winnerOutcomeIndex = typeof marketData?.winnerOutcomeIndex === 'number' && Number.isInteger(marketData.winnerOutcomeIndex) ? marketData.winnerOutcomeIndex : null;
  const liquidityModelByOutcome = new Map<number, CurrentChartLiquidityModel | null>(rows.map(outcome => {
    const outcomeIndex = Number(outcome.outcomeIndex);
    return [outcomeIndex, resolveCurrentChartLiquidityModel({
      marketStatus: marketData?.status,
      outcomeIndex,
      winnerOutcomeIndex,
      hasCpSwapPool: outcome.graduated === true
    })];
  }));
  const currentCpMarketCapRows = rows.flatMap(outcome => {
    const outcomeIndex = Number(outcome.outcomeIndex);
    if (liquidityModelByOutcome.get(outcomeIndex) !== 'cp_swap') return [];
    return [{
      outcomeIndex,
      valueD6: currentCpMarketCapD6({
        priceD9: outcome.price,
        indexedOutcomeMintSupplyD6: outcome.totalSupply
      })
    }];
  });
  return {
    rows,
    outcomeCount,
    renderedOutcomeIndices: getRenderedOutcomeIndices(outcomeCount),
    winnerOutcomeIndex,
    liquidityModelByOutcome,
    indexedSupplyByOutcome: new Map(rows.map(outcome => [Number(outcome.outcomeIndex), outcome.totalSupply])),
    currentCpMarketCapByOutcome: new Map(currentCpMarketCapRows.flatMap(({
      outcomeIndex,
      valueD6
    }) => valueD6 === undefined ? [] : [[outcomeIndex, valueD6]])),
    unavailableCpMarketCapOutcomeIndicesKey: currentCpMarketCapRows.filter(({
      valueD6
    }) => valueD6 === undefined).map(({
      outcomeIndex
    }) => outcomeIndex).join(',')
  };
}
function buildConnectedBreakevens(args: {
  readonly rows: ReturnType<typeof useMarketPositions>['rows'];
  readonly hiddenOutcomes: ReadonlyArray<number>;
  readonly outcomes: ReadonlyArray<ConnectedOutcomeRow>;
  readonly onchainTradeData: ReturnType<typeof useOnchainTradeData>['data'];
  readonly liquidityModelByOutcome: ReadonlyMap<number, CurrentChartLiquidityModel | null>;
}): ReadonlyArray<{
  readonly outcomeIndex: number;
  readonly priceUsdc: number;
  readonly marketCapUsdc?: number;
}> {
  return args.rows.flatMap(row => {
    if (args.hiddenOutcomes.includes(row.outcomeIndex)) return [];
    if (!/^\d+$/.test(row.balance) || BigInt(row.balance) <= BigInt(0)) return [];
    const priceD9 = averageEntryPriceD9(row);
    const priceUsdc = Number(priceD9) / D9_SCALE;
    if (!Number.isFinite(priceUsdc) || priceUsdc <= 0) return [];
    const snapshot = args.outcomes.find(outcome => Number(outcome.outcomeIndex) === row.outcomeIndex);
    const curve = args.onchainTradeData?.outcomes.find(outcome => outcome.outcomeIndex === row.outcomeIndex)?.bondingCurve;
    const liquidityModel = args.liquidityModelByOutcome.get(row.outcomeIndex);
    const marketCapD6 = snapshot === undefined || liquidityModel === undefined || liquidityModel === null ? undefined : breakEvenMarketCapD6(priceD9, {
      graduated: liquidityModel === 'cp_swap',
      indexedOutcomeMintSupplyD6: snapshot.totalSupply,
      initialOutcomeSupplyD6: curve?.initialOutcomeSupplyD6,
      virtualQuoteD6: curve?.virtualQuoteReserveD6
    });
    const marketCapUsdc = marketCapD6 === undefined ? undefined : Number(marketCapD6) / D6_SCALE;
    return [{
      outcomeIndex: row.outcomeIndex,
      priceUsdc,
      ...(marketCapUsdc === undefined ? {} : {
        marketCapUsdc
      })
    }];
  });
}
function useCpMarketCapAvailabilityLog(marketPubkey, outcomeIndicesKey) {
  const $ = _c(4);
  let t0;
  let t1;
  if ($[0] !== marketPubkey || $[1] !== outcomeIndicesKey) {
    t0 = () => {
      if (outcomeIndicesKey === "") {
        return;
      }
      logger.warn("chart.cp_current_market_cap_unavailable", {
        marketPubkey,
        outcomeIndices: outcomeIndicesKey.split(",").map(Number)
      });
    };
    t1 = [marketPubkey, outcomeIndicesKey];
    $[0] = marketPubkey;
    $[1] = outcomeIndicesKey;
    $[2] = t0;
    $[3] = t1;
  } else {
    t0 = $[2];
    t1 = $[3];
  }
  useEffect(t0, t1);
}
type ConnectedTimeframeModel = ReturnType<typeof resolveTerminalChartLifecycle> & {
  readonly renderMode: 'line' | 'candle';
  readonly backendInterval: ConcreteChartInterval | 'all';
  readonly anchorMs: number;
  readonly anchorSec: number;
  readonly lifespanSecs: number;
  readonly marketCreatedAtRaw: number | undefined;
  readonly marketCreatedAt: number;
  readonly dataAvailableIntervals: ReadonlySet<ChartInterval> | undefined;
  readonly chartFrom: string | undefined;
  readonly chartTo: string | undefined;
  readonly liveWindowSeconds: string | undefined;
  readonly targetBars: number;
  readonly userTradesFrom: string;
  readonly backendChartType: 'line' | 'ohlcv';
  readonly backendProperty: string;
  readonly chartSeriesKey: string;
  readonly chartMetricTransitionGroupKey: string;
  readonly chartDataRequestKey: string;
  readonly terminalOpening: {
    readonly from: string;
    readonly to: string;
    readonly requestKey: string;
  } | undefined;
  readonly chartWindowSecs: number;
  readonly chartWindowIdentity: string;
};
function buildConnectedTimeframeModel(args: {
  readonly marketPubkey: string;
  readonly lifecycleStatus: string;
  readonly lifecycleCreatedAt: string;
  readonly lifecycleTradingStartedAt: string | null;
  readonly lifecycleResolvedAt: string | null;
  readonly tradesFrom: string;
  readonly isAuthenticated: boolean;
  readonly readOnly: boolean;
  readonly chartMode: ChartMode;
  readonly yAxisProperty: YAxisProperty;
  readonly intervalStore: ChartInterval;
  readonly renderModeStore: 'line' | 'candle';
  readonly renderedOutcomeIndices: readonly number[];
  readonly nowMinuteMs: number;
  readonly allSnapshotToMs: number;
  readonly plotWidth: number | null;
}): ConnectedTimeframeModel {
  const currentSecond = Math.floor(Date.now() / 1000);
  // Proposal age must not widen All's plotted span or its requested bucket.
  const marketCreatedAtRaw = isoToUnix(args.lifecycleTradingStartedAt ?? args.lifecycleCreatedAt);
  const marketCreatedAt = marketCreatedAtRaw === undefined ? currentSecond : Math.min(marketCreatedAtRaw, currentSecond);
  const requestedLifecycle = resolveTerminalChartLifecycle({
    status: args.lifecycleStatus,
    resolvedAt: args.lifecycleResolvedAt,
    persistedInterval: args.intervalStore,
    persistedRenderMode: args.renderModeStore,
    readOnly: args.readOnly
  });
  const renderMode = requestedLifecycle.renderMode;
  const anchorMs = requestedLifecycle.resolutionUnix === undefined ? requestedLifecycle.interval === 'all' ? Math.max(args.nowMinuteMs, args.allSnapshotToMs) : args.nowMinuteMs : requestedLifecycle.resolutionUnix * 1000;
  const anchorSec = Math.floor(anchorMs / 1000);
  const lifespanSecs = Math.max(0, anchorSec - marketCreatedAt);
  const dataAvailableIntervals = getStableAvailableIntervals(marketCreatedAtRaw, marketCreatedAt, lifespanSecs);
  const lifecycle = {
    ...requestedLifecycle,
    interval: resolveAvailableInterval(requestedLifecycle.interval, dataAvailableIntervals)
  };
  const backendInterval = lifecycle.interval === 'live' ? '1m' : 'all';
  const terminalAllRange = lifecycle.locked && lifecycle.interval === 'all' && lifecycle.resolutionUnix !== undefined && marketCreatedAtRaw !== undefined ? {
    from: new Date(marketCreatedAtRaw * 1000).toISOString(),
    to: new Date(lifecycle.resolutionUnix * 1000).toISOString()
  } : undefined;
  const chartFrom = terminalAllRange !== undefined ? terminalAllRange.from : resolveChartRequestStart({
    interval: lifecycle.interval,
    marketCreatedAt,
    anchorMs,
    lifespanSecs
  });
  const createdAtIso = args.lifecycleTradingStartedAt ?? args.lifecycleCreatedAt;
  const userTradesFrom = !args.isAuthenticated || lifecycle.interval === 'live' ? args.tradesFrom : chartFrom ?? (typeof createdAtIso === 'string' && createdAtIso.length > 0 ? createdAtIso : args.tradesFrom);
  const backendChartType = args.chartMode === 'live' && toBackendChartType(renderMode) === 'ohlcv' ? 'ohlcv' : 'line';
  const targetBars = backendChartType === 'ohlcv' ? candleChartTargetBars(args.plotWidth ?? 0, lifecycle.interval) : priceChartTargetBars(args.plotWidth ?? 0, 'line');
  const backendProperty = args.chartMode === 'consensus' ? 'twar' : toBackendProperty(args.yAxisProperty);
  // Each density owner needs a post-interest baseline through server request time.
  // Reusing hover's end would omit trades before this owner's NATS subscription.
  const chartTo = args.chartMode === 'live' && !lifecycle.locked ? undefined : terminalAllRange !== undefined ? terminalAllRange.to : lifecycle.interval === 'all' ? new Date(Math.max(args.allSnapshotToMs, marketCreatedAt * 1000 + 1_000)).toISOString() : exclusiveChartRequestEnd(lifecycle.resolutionUnix === undefined ? anchorMs : lifecycle.resolutionUnix * 1000);
  const outcomeKey = args.renderedOutcomeIndices.join(',');
  const lifecycleKey = lifecycle.locked ? 'terminal' : 'open';
  const chartSeriesKey = [args.marketPubkey, lifecycleKey, backendProperty, backendChartType, outcomeKey].join('|');
  // Market Share uses consensus mode, but is still a compatible line surface.
  // Retained frames carry their own metric units and chart mode.
  const chartMetricTransitionGroupKey = [args.marketPubkey, lifecycleKey, backendChartType, lifecycle.interval, outcomeKey].join('|');
  const liveWindowSeconds = undefined;
  const chartDataRequestKey = lifecycle.interval === 'live' ? [chartSeriesKey, lifecycle.interval].join('|') : [chartSeriesKey, lifecycle.interval, backendInterval, targetBars, liveWindowSeconds ?? chartFrom ?? '', liveWindowSeconds === undefined ? chartTo ?? '' : 'rolling'].join('|');
  const terminalOpeningEndUnix = lifecycle.locked && lifecycle.interval === 'all' ? resolveTerminalChartOpeningEndUnix({
    marketCreatedAtUnix: marketCreatedAtRaw,
    resolvedAtUnix: isoToUnix(args.lifecycleResolvedAt)
  }) : undefined;
  const terminalOpening = terminalOpeningEndUnix === undefined || marketCreatedAtRaw === undefined ? undefined : {
    from: new Date(marketCreatedAtRaw * 1000).toISOString(),
    to: new Date(terminalOpeningEndUnix * 1000).toISOString(),
    requestKey: `${chartDataRequestKey}|terminal-opening|${marketCreatedAtRaw}|${terminalOpeningEndUnix}`
  };
  const chartWindowSecs = windowSecondsForTimeframe(lifecycle.interval, lifespanSecs);
  return {
    ...lifecycle,
    renderMode,
    backendInterval,
    anchorMs,
    anchorSec,
    lifespanSecs,
    marketCreatedAtRaw,
    marketCreatedAt,
    dataAvailableIntervals,
    chartFrom,
    chartTo,
    liveWindowSeconds,
    targetBars,
    userTradesFrom,
    backendChartType,
    backendProperty,
    chartSeriesKey,
    chartMetricTransitionGroupKey,
    chartDataRequestKey,
    terminalOpening,
    chartWindowSecs,
    chartWindowIdentity: `${args.marketPubkey}|${lifecycle.interval}|${marketCreatedAtRaw ?? 'pending'}|${lifecycle.resolutionUnix ?? 'live'}`
  };
}
type ConnectedTradeRow = ReturnType<typeof useMarketTradeStreams>['tapeRows'][number];
interface ConnectedLiveChartDataArgs {
  readonly liveCandleEnabled: boolean;
  readonly liveCandleNowSec: number;
  readonly yAxisProperty: YAxisProperty;
  readonly allTradesRows: ReadonlyArray<ConnectedTradeRow>;
  readonly indexedOutcomeMintSupplyD6ByOutcome: ReadonlyMap<number, string | null | undefined>;
  readonly initialOutcomeSupplyD6ByOutcome: ReadonlyMap<number, string | null | undefined>;
  readonly virtualQuoteD6ByOutcome: ReadonlyMap<number, string | null | undefined>;
  readonly outcomeCount: number;
  readonly chartMode: ChartMode;
  readonly marketCapLiveEnabled: boolean;
  readonly liveStepLine: boolean;
  readonly outcomeRows: ReadonlyArray<ConnectedOutcomeRow>;
  readonly currentLiquidityModelByOutcome: ReadonlyMap<number, CurrentChartLiquidityModel | null>;
  readonly currentCpMarketCapD6ByOutcome: ReadonlyMap<number, bigint>;
  readonly outcomes: ReadonlyArray<MarketChartOutcome> | undefined;
  readonly marketCapSeedData: Readonly<Record<number, ChartLeafResult | undefined>>;
}
function buildConnectedLiveChartData(args: ConnectedLiveChartDataArgs, trajectories: LiveMarketCapTrajectories): Record<number, MarketChartData> {
  const cutoffSec = (args.liveCandleEnabled ? args.liveCandleNowSec : Math.floor(Date.now() / 1000)) - LIVE_WINDOW_SECONDS;
  const liveChartDataByOutcome = args.liveCandleEnabled ? buildConnectedLiveCandles(args, cutoffSec) : buildConnectedLiveLines(args, trajectories, cutoffSec);
  const chartData: Record<number, MarketChartData> = {};
  for (let index = 0; index < args.outcomeCount; index += 1) {
    const liveEntry = liveChartDataByOutcome[index];
    chartData[index] = {
      line: liveEntry?.line ?? [],
      ...(liveEntry?.ohlcv === undefined ? {} : {
        ohlcv: liveEntry.ohlcv
      })
    };
  }
  return chartData;
}
function buildConnectedLiveCandles(args: ConnectedLiveChartDataArgs, cutoffSec: number): Record<number, ChartLeafResult> {
  const candlesByOutcome = buildLiveTradeCandles({
    trades: args.allTradesRows,
    cutoffSec,
    nowSec: args.liveCandleNowSec,
    mode: args.yAxisProperty === 'marketCap' ? 'marketCap' : 'price',
    indexedOutcomeMintSupplyD6ByOutcome: args.indexedOutcomeMintSupplyD6ByOutcome,
    initialOutcomeSupplyD6ByOutcome: args.initialOutcomeSupplyD6ByOutcome,
    virtualQuoteD6ByOutcome: args.virtualQuoteD6ByOutcome
  });
  const result: Record<number, ChartLeafResult> = {};
  for (let index = 0; index < args.outcomeCount; index += 1) {
    const ohlcv = candlesByOutcome[index] ?? [];
    result[index] = {
      line: ohlcv.map(candle => ({
        time: candle.time,
        value: candle.close
      })),
      ohlcv,
      bucketSeconds: LIVE_CANDLE_BUCKET_SECONDS
    };
  }
  return result;
}
function buildConnectedLiveLines(args: ConnectedLiveChartDataArgs, trajectories: LiveMarketCapTrajectories, cutoffSec: number): Record<number, ChartLeafResult> {
  const pointsByOutcome: Record<number, Array<{
    time: number;
    value: number;
  }>> = {};
  const isLivePriceMode = args.chartMode === 'live' && args.yAxisProperty === 'price';
  if (isLivePriceMode || args.marketCapLiveEnabled) {
    addConnectedLiveTradeLines(args, pointsByOutcome, cutoffSec, isLivePriceMode);
    addConnectedQuietPoolSeeds(args, pointsByOutcome, cutoffSec);
    addConnectedBondingCurveTrajectories(args, pointsByOutcome, trajectories, cutoffSec);
  }
  return Object.fromEntries(Array.from({
    length: args.outcomeCount
  }, (_, index) => [index, {
    line: (pointsByOutcome[index] ?? []).toSorted((left, right) => left.time - right.time)
  }]));
}
function addConnectedLiveTradeLines(args: ConnectedLiveChartDataArgs, pointsByOutcome: Record<number, Array<{
  time: number;
  value: number;
}>>, cutoffSec: number, isLivePriceMode: boolean): void {
  const currentPriceByOutcome = new Map<number, number>();
  if (isLivePriceMode) {
    for (const outcome of args.outcomeRows) {
      const currentPrice = currentOutcomeSpotChartValue(String(outcome.price));
      if (currentPrice !== undefined) {
        currentPriceByOutcome.set(Number(outcome.outcomeIndex), currentPrice);
      }
    }
  }
  const liveTradeSeries = buildLiveTradeSeries({
    trades: args.allTradesRows,
    cutoffSec,
    mode: args.marketCapLiveEnabled ? 'marketCap' : 'price',
    liveStepLine: args.liveStepLine,
    ...(isLivePriceMode ? {
      fallbackTime: cutoffSec,
      fallbackValueByOutcome: currentPriceByOutcome
    } : {}),
    indexedOutcomeMintSupplyD6ByOutcome: args.indexedOutcomeMintSupplyD6ByOutcome,
    initialOutcomeSupplyD6ByOutcome: args.initialOutcomeSupplyD6ByOutcome,
    virtualQuoteD6ByOutcome: args.virtualQuoteD6ByOutcome
  });
  for (const [outcomeIndex, points] of Object.entries(liveTradeSeries)) {
    pointsByOutcome[Number(outcomeIndex)] = points;
  }
}
function addConnectedQuietPoolSeeds(args: ConnectedLiveChartDataArgs, pointsByOutcome: Record<number, Array<{
  time: number;
  value: number;
}>>, cutoffSec: number): void {
  for (const outcome of args.outcomeRows) {
    const outcomeIndex = Number(outcome.outcomeIndex);
    if (args.currentLiquidityModelByOutcome.get(outcomeIndex) !== 'cp_swap') continue;
    if ((pointsByOutcome[outcomeIndex]?.length ?? 0) > 0) continue;
    if (args.marketCapLiveEnabled) {
      const currentMarketCapD6 = args.currentCpMarketCapD6ByOutcome.get(outcomeIndex);
      if (currentMarketCapD6 !== undefined) {
        pointsByOutcome[outcomeIndex] = [{
          time: cutoffSec,
          value: d6ToNum(currentMarketCapD6.toString())
        }];
      }
      continue;
    }
    const currentPrice = currentOutcomeSpotChartValue(String(outcome.price));
    if (currentPrice !== undefined) {
      pointsByOutcome[outcomeIndex] = [{
        time: cutoffSec,
        value: currentPrice
      }];
    }
  }
}
function addConnectedBondingCurveTrajectories(args: ConnectedLiveChartDataArgs, pointsByOutcome: Record<number, Array<{
  time: number;
  value: number;
}>>, trajectories: LiveMarketCapTrajectories, cutoffSec: number): void {
  if (!args.marketCapLiveEnabled) return;
  const currentMarketCapByOutcome = new Map((args.outcomes ?? []).map(outcome => [outcome.index, outcome.marketCap] as const));
  for (let index = 0; index < args.outcomeCount; index += 1) {
    if (args.currentLiquidityModelByOutcome.get(index) === 'cp_swap') continue;
    pointsByOutcome[index] = buildLiveMarketCapTrajectorySeries({
      seed: args.marketCapSeedData[index]?.line ?? [],
      trajectory: trajectories.marketCap[index] ?? [],
      cutoffSec,
      fallbackValue: currentMarketCapByOutcome.get(index)
    });
  }
}
interface ConnectedLiveValuesArgs {
  readonly locked: boolean;
  readonly consensusStopTimestamp: number | undefined;
  readonly terminalValues: Record<number, number> | undefined;
  readonly chartMode: ChartMode;
  readonly yAxisProperty: YAxisProperty;
  readonly onchainTradeData: ReturnType<typeof useOnchainTradeData>['data'];
  readonly outcomeRows: ReadonlyArray<ConnectedOutcomeRow>;
  readonly convictionMarket: ConvictionMarketFields;
  readonly currentLiquidityModelByOutcome: ReadonlyMap<number, CurrentChartLiquidityModel | null>;
  readonly currentCpMarketCapD6ByOutcome: ReadonlyMap<number, bigint>;
  readonly marketCapSeedData: Readonly<Record<number, ChartLeafResult | undefined>>;
}
function resolveConnectedLiveValues(args: ConnectedLiveValuesArgs): Record<number, number> | undefined {
  if (args.consensusStopTimestamp !== undefined) return args.terminalValues;
  if (args.locked) return undefined;
  if (args.chartMode === 'consensus') {
    return Object.fromEntries(args.outcomeRows.map(outcome => [Number(outcome.outcomeIndex), outcomeConvictionFraction(outcome, args.convictionMarket)]));
  }
  if (args.chartMode !== 'live') return undefined;
  const isPrice = args.yAxisProperty === 'price';
  const isMarketCap = args.yAxisProperty === 'marketCap';
  if (!isPrice && !isMarketCap) return undefined;
  const poolSnapshotOutcomeByIndex = new Map((args.onchainTradeData?.outcomes ?? []).map(outcome => [outcome.outcomeIndex, outcome] as const));
  const values: Record<number, number> = {};
  for (const outcome of args.outcomeRows) {
    addConnectedLiveValue(args, values, poolSnapshotOutcomeByIndex, outcome, isPrice);
  }
  return Object.keys(values).length > 0 ? values : undefined;
}
function addConnectedLiveValue(args: ConnectedLiveValuesArgs, values: Record<number, number>, poolSnapshotOutcomeByIndex: ReadonlyMap<number, NonNullable<ConnectedLiveValuesArgs['onchainTradeData']>['outcomes'][number]>, outcome: ConnectedOutcomeRow, isPrice: boolean): void {
  const outcomeIndex = Number(outcome.outcomeIndex);
  const liquidityModel = args.currentLiquidityModelByOutcome.get(outcomeIndex);
  if (isPrice) {
    const currentPrice = currentOutcomeSpotChartValue(String(outcome.price));
    if (liquidityModel !== null && liquidityModel !== undefined && currentPrice !== undefined) {
      values[outcomeIndex] = currentPrice;
    }
    return;
  }
  if (liquidityModel === 'cp_swap') {
    const currentMarketCapD6 = args.currentCpMarketCapD6ByOutcome.get(outcomeIndex);
    if (currentMarketCapD6 !== undefined) {
      values[outcomeIndex] = d6ToNum(currentMarketCapD6.toString());
    }
    return;
  }
  if (liquidityModel !== 'bonding_curve') return;
  const virtualQuoteD6 = args.marketCapSeedData[outcomeIndex]?.virtualQuote;
  const poolQuoteReserveD6 = poolSnapshotOutcomeByIndex.get(outcomeIndex)?.bondingCurve?.poolQuoteReserveD6;
  if (virtualQuoteD6 === undefined || typeof poolQuoteReserveD6 !== 'string' || !/^\d+$/.test(poolQuoteReserveD6) || !/^\d+$/.test(virtualQuoteD6)) {
    return;
  }
  const marketCap = bondingCurveMarketCapFromQuoteReserveD6({
    poolQuoteReserveD6: BigInt(poolQuoteReserveD6),
    virtualQuoteReserveD6: BigInt(virtualQuoteD6)
  });
  if (marketCap.available && marketCap.value > BigInt(0)) {
    values[outcomeIndex] = Number(marketCap.value) / D6_SCALE;
  }
}
function resolveAuthenticatedViewerPubkey(isAuthenticated: boolean, walletAddress: string | undefined): string | undefined {
  return isAuthenticated && walletAddress !== undefined ? walletAddress : undefined;
}
function resolveConnectedChartMode(readOnly: boolean, storedMode: ChartMode): ChartMode {
  return readOnly ? 'consensus' : storedMode;
}
interface ConnectedOnchainModel {
  readonly data: ReturnType<typeof useOnchainTradeData>['data'];
  readonly initialOutcomeSupplyD6ByOutcome: ReadonlyMap<number, string | null | undefined>;
  readonly virtualQuoteD6ByOutcome: ReadonlyMap<number, string | null | undefined>;
}
function useConnectedOnchainModel(args) {
  const $ = _c(11);
  const enabled = args.showBreakEven || args.yAxisProperty === "marketCap";
  let t0;
  if ($[0] !== args.outcomeRows || $[1] !== enabled) {
    t0 = enabled ? args.outcomeRows.map(_temp2) : [];
    $[0] = args.outcomeRows;
    $[1] = enabled;
    $[2] = t0;
  } else {
    t0 = $[2];
  }
  const data = useOnchainTradeData(enabled ? args.marketPubkey : undefined, enabled ? args.outcomeRows.length : 0, t0, enabled ? args.marketData : undefined).data;
  let t1;
  if ($[3] !== data?.outcomes) {
    t1 = new Map((data?.outcomes ?? []).map(_temp3));
    $[3] = data?.outcomes;
    $[4] = t1;
  } else {
    t1 = $[4];
  }
  let t2;
  if ($[5] !== data?.outcomes) {
    t2 = new Map((data?.outcomes ?? []).map(_temp4));
    $[5] = data?.outcomes;
    $[6] = t2;
  } else {
    t2 = $[6];
  }
  let t3;
  if ($[7] !== data || $[8] !== t1 || $[9] !== t2) {
    t3 = {
      data,
      initialOutcomeSupplyD6ByOutcome: t1,
      virtualQuoteD6ByOutcome: t2
    };
    $[7] = data;
    $[8] = t1;
    $[9] = t2;
    $[10] = t3;
  } else {
    t3 = $[10];
  }
  return t3;
}
function _temp4(outcome_1) {
  return [outcome_1.outcomeIndex, outcome_1.bondingCurve?.virtualQuoteReserveD6];
}
function _temp3(outcome_0) {
  return [outcome_0.outcomeIndex, outcome_0.bondingCurve?.initialOutcomeSupplyD6];
}
function _temp2(outcome) {
  return {
    outcomeIndex: Number(outcome.outcomeIndex),
    outcomeMint: String(outcome.mint ?? "")
  };
}
function useSelectedChartWindow(identity, seconds) {
  const $ = _c(3);
  let t0;
  if ($[0] !== identity || $[1] !== seconds) {
    t0 = {
      identity,
      seconds
    };
    $[0] = identity;
    $[1] = seconds;
    $[2] = t0;
  } else {
    t0 = $[2];
  }
  const [selected, setSelected] = useState(t0);
  if (selected.identity !== identity) {
    setSelected({
      identity,
      seconds
    });
  }
  return selected;
}
function buildMarketTradeStreamArgs(args: {
  readonly marketPubkey: string;
  readonly tapeFrom: string;
  readonly viewerFrom: string;
  readonly viewerWallet: string | undefined;
}): Parameters<typeof useMarketTradeStreams>[0] {
  return {
    market: args.marketPubkey,
    tapeFrom: args.tapeFrom,
    viewerFrom: args.viewerFrom,
    ...(args.viewerWallet === undefined ? {} : {
      viewerWallet: args.viewerWallet
    })
  };
}
function buildConnectedOutcomes(rows: ReadonlyArray<ConnectedOutcomeRow>, convictionMarket: ConvictionMarketFields): ReadonlyArray<MarketChartOutcome> | undefined {
  if (rows.length === 0) return undefined;
  return rows.map(outcome => ({
    ...mapOutcomeToChartOutcome(outcome, outcome.label),
    twar: outcomeConvictionFraction(outcome, convictionMarket)
  }));
}
function buildConnectedUserTrades(rows: ReturnType<typeof useMarketTradeStreams>['viewerRows'], viewerPubkey: string | undefined): TradeAnnotation[] {
  if (viewerPubkey === undefined) return [];
  return rows.flatMap(trade => trade.trader?.walletAddress === viewerPubkey ? [mapTradeToAnnotation(trade)] : []);
}
function shouldPageLiveTradeTape(interval: ChartInterval, rows: ReturnType<typeof useMarketTradeStreams>['tapeRows'], cutoffMs: number, candleMode: boolean, lastFetchedTimestamp: string | undefined): boolean {
  if (interval === 'live' && candleMode) {
    // A delayed realtime trade can supply a line anchor while REST pages are missing.
    // Only the SDK's REST tail proves the whole edge candle was downloaded;
    // equal timestamps may still span the next page.
    return lastFetchedTimestamp === undefined || !(Date.parse(lastFetchedTimestamp) < cutoffMs);
  }
  return interval === 'live' && liveTradePagesNeedMore(rows, cutoffMs);
}
interface ConnectedLiveFlags {
  readonly marketCapLiveEnabled: boolean;
  readonly liveCandleEnabled: boolean;
}
function resolveConnectedLiveFlags(args: {
  readonly interval: ChartInterval;
  readonly chartMode: ChartMode;
  readonly yAxisProperty: YAxisProperty;
  readonly renderMode: 'line' | 'candle';
  readonly readOnly: boolean;
}): ConnectedLiveFlags {
  return {
    marketCapLiveEnabled: args.interval === 'live' && args.chartMode === 'live' && args.yAxisProperty === 'marketCap' && args.renderMode !== 'candle' && !args.readOnly,
    liveCandleEnabled: args.interval === 'live' && args.chartMode === 'live' && args.renderMode === 'candle' && !args.readOnly
  };
}
function resolveConnectedChartReadiness(args: {
  readonly interval: ChartInterval;
  readonly outcomeCount: number;
  readonly renderedOutcomeIndices: readonly number[];
  readonly marketCapLiveEnabled: boolean;
  readonly marketCapSeedData: Readonly<Record<number, ChartLeafResult | undefined>>;
  readonly tradesPending: boolean;
  readonly chartLeafData: Readonly<Record<number, ChartLeafResult | undefined>>;
  readonly chartDataRequestKey: string;
  readonly terminalOpening: ConnectedTimeframeModel['terminalOpening'];
  readonly terminalOpeningData: Readonly<Record<number, ChartLeafResult | undefined>>;
}): {
  readonly liveChartReady: boolean;
  readonly targetChartReady: boolean;
} {
  const terminalOpeningReady = args.terminalOpening === undefined || args.renderedOutcomeIndices.every(outcomeIndex => args.terminalOpeningData[outcomeIndex]?.requestKey === args.terminalOpening?.requestKey);
  const marketCapSeedReady = args.renderedOutcomeIndices.every(outcomeIndex => args.marketCapSeedData[outcomeIndex] !== undefined);
  const liveChartReady = args.outcomeCount > 0 && !args.tradesPending && (!args.marketCapLiveEnabled || marketCapSeedReady);
  const historicalReady = args.renderedOutcomeIndices.length > 0 && args.renderedOutcomeIndices.every(outcomeIndex => args.chartLeafData[outcomeIndex]?.requestKey === args.chartDataRequestKey) && terminalOpeningReady;
  return {
    liveChartReady,
    targetChartReady: args.interval === 'live' ? liveChartReady : historicalReady
  };
}
function resolveConnectedWindowSeconds(args: {
  readonly locked: boolean;
  readonly anchorSec: number;
  readonly marketCreatedAtRaw: number | undefined;
  readonly historyStartUnix: number | undefined;
  readonly chartWindowSecs: number;
  readonly interval: ChartInterval;
  readonly selectedChartWindow: {
    readonly identity: string;
    readonly seconds: number;
  };
  readonly chartWindowIdentity: string;
}): number {
  if (args.locked) {
    return resolveTerminalChartWindowSecs({
      locked: true,
      anchorUnix: args.anchorSec,
      marketCreatedAtUnix: args.marketCreatedAtRaw,
      historyStartUnix: args.historyStartUnix,
      fallbackWindowSecs: args.chartWindowSecs
    });
  }
  if (args.interval === 'all' && args.selectedChartWindow.identity === args.chartWindowIdentity) {
    return args.selectedChartWindow.seconds;
  }
  return args.chartWindowSecs;
}
function resolveConnectedConsensusDisplay(args: {
  readonly chartData: Record<number, MarketChartData>;
  readonly threshold: number | undefined;
  readonly chartMode: ChartMode;
  readonly resolutionUnix: number | undefined;
  readonly winnerOutcomeIndex: number | null;
  readonly outcomeRows: readonly ConnectedOutcomeRow[];
}): ReturnType<typeof stopConsensusChartAtThreshold<MarketChartData>> & {
  readonly missingSnapshotOutcomeIndices?: readonly number[];
} {
  if (args.chartMode === 'consensus' && args.resolutionUnix !== undefined) {
    return resolveTerminalConsensusSnapshot({
      ...args,
      outcomes: args.outcomeRows,
      resolutionUnix: args.resolutionUnix
    });
  }
  return stopConsensusChartAtThreshold({
    chartData: args.chartData,
    threshold: args.threshold,
    enabled: args.chartMode === 'consensus' && args.resolutionUnix !== undefined,
    ...(args.winnerOutcomeIndex === null ? {} : {
      preferredOutcomeIndex: args.winnerOutcomeIndex
    })
  });
}
function resolveConnectedResolutionMarker(chartMode: ChartMode, stopTimestamp: number | undefined, resolutionUnix: number | undefined): number | undefined {
  return chartMode === 'consensus' && stopTimestamp !== undefined ? stopTimestamp : resolutionUnix;
}
function resolveNullableChartData(chartData: Record<number, MarketChartData> | null): Record<number, MarketChartData> {
  return chartData ?? {};
}
function resolveRenderableChartData(chartData: Record<number, MarketChartData>): Record<number, MarketChartData> | null {
  return Object.keys(chartData).length > 0 ? chartData : null;
}
function resolveConnectedCandleModel(args: {
  readonly selectedOutcome: number | null | undefined;
  readonly backendChartType: 'line' | 'ohlcv';
  readonly interval: ChartInterval;
  readonly adaptiveBucketSeconds: number | undefined;
}): {
  readonly focusedOutcomeIndex: number;
  readonly marketChartMode: 'line' | 'candle';
  readonly candleWidthSeconds: number;
} {
  const candleWidthSeconds = args.interval === 'live' ? LIVE_CANDLE_BUCKET_SECONDS : args.adaptiveBucketSeconds ?? 60;
  return {
    focusedOutcomeIndex: args.selectedOutcome ?? 0,
    marketChartMode: args.backendChartType === 'ohlcv' ? 'candle' : 'line',
    candleWidthSeconds
  };
}
function buildConnectedOutcomesWithColor(args: {
  readonly outcomes: ReadonlyArray<MarketChartOutcome> | undefined;
  readonly hues: ReturnType<typeof useOutcomeColor>;
  readonly seriesColorVar: string | undefined;
}): ConnectedChartModel['v2OutcomesWithColor'] {
  return (args.outcomes ?? []).map((outcome, index) => ({
    index: outcome.index,
    title: outcome.title,
    price: outcome.price,
    color: args.seriesColorVar ?? (args.hues[index] ? outcomeHueToStepVar(args.hues[index].hue, 400) : '')
  }));
}
function buildOutcomeColorInputs(outcomes: ReadonlyArray<MarketChartOutcome> | undefined): ReadonlyArray<{
  readonly title: string;
  readonly index: number;
}> {
  return (outcomes ?? []).map(outcome => ({
    title: outcome.title,
    index: outcome.index
  }));
}
function resolveConnectedCandleNowSec(args: {
  readonly resolutionMarkerUnix: number | undefined;
  readonly chartRightEdgeSec: number;
  readonly liveCandleNowSec: number;
}): number {
  if (args.resolutionMarkerUnix !== undefined) return args.chartRightEdgeSec;
  return args.liveCandleNowSec;
}

/**
 * The chart container's full data orchestration — market + per-outcome chart
 * + trade SDK reads, the retained-timeframe owners, the live-tick
 * derivation, and the SDK→MarketChart mapping. Kept as a hook in this file so every file-keyed
 * verified-FP exemption stays valid (no-event-handler, refs, no-pass-data-to-
 * parent, no-prop-callback-in-effect, purity — all exempted file-wide).
 */
function useConnectedChart(props) {
  const $ = _c(124);
  const intentOpening = useChartIntentOpening(props.marketPubkey);
  let t0;
  if ($[0] !== intentOpening || $[1] !== props.opening || $[2] !== props.readOnly) {
    t0 = () => props.readOnly ? props.opening : intentOpening ?? props.opening;
    $[0] = intentOpening;
    $[1] = props.opening;
    $[2] = props.readOnly;
    $[3] = t0;
  } else {
    t0 = $[3];
  }
  const [opening] = useState(t0);
  const [plotWidth, setPlotWidth] = useState(null);
  let t1;
  if ($[4] !== plotWidth) {
    t1 = plotWidth === null ? 180 : visibleBarEstimate(plotWidth, "line");
    $[4] = plotWidth;
    $[5] = t1;
  } else {
    t1 = $[5];
  }
  const marketShareSamples = t1;
  const queryClient = useQueryClient();
  let t2;
  if ($[6] !== opening || $[7] !== props.marketPubkey || $[8] !== queryClient) {
    t2 = () => readConvictionChartOpening(queryClient, opening, props.marketPubkey);
    $[6] = opening;
    $[7] = props.marketPubkey;
    $[8] = queryClient;
    $[9] = t2;
  } else {
    t2 = $[9];
  }
  const [openingConviction, setOpeningConviction] = useState(t2);
  let t3;
  if ($[10] !== props.readOnly) {
    t3 = s => props.readOnly || s.chartMode === "consensus" ? "twar" : toBackendProperty(s.yAxisProperty);
    $[10] = props.readOnly;
    $[11] = t3;
  } else {
    t3 = $[11];
  }
  const openingProperty = useChartSettingsStore(t3);
  let t4;
  if ($[12] !== opening || $[13] !== openingProperty || $[14] !== props.marketPubkey || $[15] !== queryClient) {
    t4 = () => readMarketChartOpening(queryClient, opening, props.marketPubkey, "main", openingProperty);
    $[12] = opening;
    $[13] = openingProperty;
    $[14] = props.marketPubkey;
    $[15] = queryClient;
    $[16] = t4;
  } else {
    t4 = $[16];
  }
  const [openingData] = useState(t4);
  let t5;
  if ($[17] !== opening || $[18] !== openingProperty || $[19] !== props.marketPubkey || $[20] !== queryClient) {
    t5 = () => readMarketChartOpening(queryClient, opening, props.marketPubkey, "terminal", openingProperty);
    $[17] = opening;
    $[18] = openingProperty;
    $[19] = props.marketPubkey;
    $[20] = queryClient;
    $[21] = t5;
  } else {
    t5 = $[21];
  }
  const [openingTerminalData] = useState(t5);
  const {
    marketPubkey,
    debugMode: t6,
    seriesColorVar,
    readOnly: t7,
    liveStepLine: t8,
    marketData,
    lifecycleStatus,
    lifecycleCreatedAt,
    lifecycleTradingStartedAt,
    lifecycleResolvedAt
  } = props;
  const debugMode = t6 === undefined ? false : t6;
  const readOnly = t7 === undefined ? false : t7;
  const liveStepLine = t8 === undefined ? true : t8;
  const t = useTranslations("chart");
  const {
    formatD6
  } = useFormattedCurrency();
  const {
    isAuthenticated
  } = useAuth();
  const {
    activeWallet
  } = useActiveWallet();
  const authenticatedViewerPubkey = resolveAuthenticatedViewerPubkey(isAuthenticated, activeWallet?.address);
  const showBreakEven = useChartSettingsStore(_temp5);
  let t9;
  if ($[22] !== marketPubkey) {
    t9 = s_1 => s_1.hiddenOutcomes[marketPubkey] ?? EMPTY_INDICES;
    $[22] = marketPubkey;
    $[23] = t9;
  } else {
    t9 = $[23];
  }
  const hiddenOutcomes = useChartSettingsStore(t9);
  const toggleOutcome = useChartSettingsStore(_temp6);
  const ownedPositionScope = useOwnedMarketPositionScope();
  let t10;
  if ($[24] !== marketPubkey || $[25] !== ownedPositionScope.userId || $[26] !== ownedPositionScope.walletPubkeys) {
    t10 = {
      userId: ownedPositionScope.userId,
      walletPubkeys: ownedPositionScope.walletPubkeys,
      market: marketPubkey
    };
    $[24] = marketPubkey;
    $[25] = ownedPositionScope.userId;
    $[26] = ownedPositionScope.walletPubkeys;
    $[27] = t10;
  } else {
    t10 = $[27];
  }
  const {
    rows: ownedMarketPositionRows
  } = useMarketPositions(t10);
  let t11;
  if ($[28] !== ownedPositionScope.activeWalletPubkey) {
    t11 = row => row.wallet === ownedPositionScope.activeWalletPubkey;
    $[28] = ownedPositionScope.activeWalletPubkey;
    $[29] = t11;
  } else {
    t11 = $[29];
  }
  const breakEvenRows = ownedMarketPositionRows.filter(t11);
  const intervalStore = useChartSettingsStore(_temp7);
  const chartModeStore = useChartSettingsStore(_temp8);
  const yAxisPropertyStore = useChartSettingsStore(_temp9);
  const renderModeStore = useChartSettingsStore(_temp10);
  const setChartModeStore = useChartSettingsStore(_temp11);
  const setChartIntervalStore = useChartSettingsStore(_temp12);
  const setYAxisPropertyStore = useChartSettingsStore(_temp13);
  const chartMode = resolveConnectedChartMode(readOnly, chartModeStore);
  const yAxisProperty = yAxisPropertyStore;
  const setChartMode = setChartModeStore;
  const setYAxisProperty = setYAxisPropertyStore;
  const [renderedChartFrameStore] = useState(_temp14);
  const [timeframeTransitionFrame, setTimeframeTransitionFrame] = useState(null);
  const [timeframeTransitionGeneration, setTimeframeTransitionGeneration] = useState(0);
  const outcomeState = buildConnectedOutcomeState(marketData);
  const {
    rows: outcomeRows,
    outcomeCount,
    renderedOutcomeIndices,
    winnerOutcomeIndex,
    liquidityModelByOutcome: currentLiquidityModelByOutcome,
    indexedSupplyByOutcome: indexedOutcomeMintSupplyD6ByOutcome,
    currentCpMarketCapByOutcome: currentCpMarketCapD6ByOutcome,
    unavailableCpMarketCapOutcomeIndicesKey
  } = outcomeState;
  useCpMarketCapAvailabilityLog(marketPubkey, unavailableCpMarketCapOutcomeIndicesKey);
  const {
    data: onchainTradeData,
    initialOutcomeSupplyD6ByOutcome,
    virtualQuoteD6ByOutcome
  } = useConnectedOnchainModel({
    showBreakEven,
    yAxisProperty,
    marketPubkey,
    outcomeRows,
    marketData
  });
  let t12;
  if ($[30] !== marketPubkey || $[31] !== opening) {
    t12 = () => opening?.marketPubkey === marketPubkey ? opening.anchorMinuteMs : Math.floor(Date.now() / 60000) * 60000;
    $[30] = marketPubkey;
    $[31] = opening;
    $[32] = t12;
  } else {
    t12 = $[32];
  }
  const [nowMinuteMs, setNowMinuteMs] = useState(t12);
  let t13;
  if ($[33] !== opening?.allSnapshotToMs) {
    t13 = () => opening?.allSnapshotToMs ?? Date.now();
    $[33] = opening?.allSnapshotToMs;
    $[34] = t13;
  } else {
    t13 = $[34];
  }
  const [allSnapshotToMs] = useState(t13);
  const timeframeArgs = {
    marketPubkey,
    lifecycleStatus,
    lifecycleCreatedAt,
    lifecycleTradingStartedAt,
    lifecycleResolvedAt,
    tradesFrom: props.tradesFrom,
    isAuthenticated,
    readOnly,
    chartMode,
    yAxisProperty,
    intervalStore,
    renderModeStore,
    renderedOutcomeIndices,
    nowMinuteMs,
    allSnapshotToMs,
    plotWidth
  };
  const timeframe = buildConnectedTimeframeModel(timeframeArgs);
  const [openingTimeframe] = useState(() => buildConnectedTimeframeModel({
    ...timeframeArgs,
    intervalStore: opening?.interval ?? intervalStore,
    chartMode: openingProperty === "twar" ? "consensus" : "live",
    renderModeStore: opening?.chartType === "ohlcv" ? "candle" : "line"
  }));
  const {
    locked,
    interval,
    resolutionUnix,
    renderMode,
    backendInterval,
    anchorSec,
    marketCreatedAtRaw,
    dataAvailableIntervals,
    chartFrom,
    chartTo,
    liveWindowSeconds,
    targetBars,
    userTradesFrom,
    backendChartType,
    backendProperty,
    chartSeriesKey,
    chartMetricTransitionGroupKey,
    chartDataRequestKey,
    terminalOpening,
    chartWindowSecs,
    chartWindowIdentity
  } = timeframe;
  const breakevens = buildConnectedBreakevens({
    rows: breakEvenRows,
    hiddenOutcomes: renderMode === "candle" ? EMPTY_INDICES : hiddenOutcomes,
    outcomes: outcomeRows,
    onchainTradeData,
    liquidityModelByOutcome: currentLiquidityModelByOutcome
  });
  const setChartInterval = function setChartInterval(nextInterval) {
    if (nextInterval === interval) {
      return;
    }
    const renderedFrame = renderedChartFrameStore.get();
    const commitInterval = () => {
      setTimeframeTransitionFrame(renderedFrame?.seriesKey === chartSeriesKey ? renderedFrame : null);
      setTimeframeTransitionGeneration(_temp15);
      setNowMinuteMs(Math.floor(Date.now() / 60000) * 60000);
      setChartIntervalStore(nextInterval);
    };
    if (renderMode === "candle") {
      commitInterval();
    } else {
      startTransition(commitInterval);
    }
  };
  let t14;
  if ($[35] === Symbol.for("react.memo_cache_sentinel")) {
    t14 = {};
    $[35] = t14;
  } else {
    t14 = $[35];
  }
  const [chartLeafData, setChartLeafData] = useState(t14);
  const [publishedTimeframe, setPublishedTimeframe] = useState(openingTimeframe);
  let t15;
  if ($[36] !== openingData || $[37] !== openingTimeframe) {
    t15 = () => Object.fromEntries(Object.entries(openingData).map(t16 => {
      const [index, entry] = t16;
      return [index, {
        ...entry,
        requestKey: openingTimeframe.chartDataRequestKey
      }];
    }));
    $[36] = openingData;
    $[37] = openingTimeframe;
    $[38] = t15;
  } else {
    t15 = $[38];
  }
  const [retainedChartLeafData, setRetainedChartLeafData] = useState(t15);
  const [previousHistoryRequestKey, setPreviousHistoryRequestKey] = useState(chartDataRequestKey);
  if (previousHistoryRequestKey !== chartDataRequestKey) {
    setPreviousHistoryRequestKey(chartDataRequestKey);
    if (renderedOutcomeIndices.length > 0 && renderedOutcomeIndices.every(index_0 => chartLeafData[index_0]?.requestKey === publishedTimeframe.chartDataRequestKey)) {
      setRetainedChartLeafData(chartLeafData);
    }
  }
  let t16;
  if ($[39] !== openingTerminalData || $[40] !== openingTimeframe) {
    t16 = () => Object.fromEntries(Object.entries(openingTerminalData).map(t17 => {
      const [index_1, entry_0] = t17;
      return [index_1, {
        ...entry_0,
        ...(openingTimeframe.terminalOpening === undefined ? {} : {
          requestKey: openingTimeframe.terminalOpening.requestKey
        })
      }];
    }));
    $[39] = openingTerminalData;
    $[40] = openingTimeframe;
    $[41] = t16;
  } else {
    t16 = $[41];
  }
  const [terminalOpeningData, setTerminalOpeningData] = useState(t16);
  let t17;
  if ($[42] !== opening?.interval || $[43] !== openingData || $[44] !== openingProperty) {
    t17 = () => opening?.interval === "live" && openingProperty === "market_cap" ? openingData : {};
    $[42] = opening?.interval;
    $[43] = openingData;
    $[44] = openingProperty;
    $[45] = t17;
  } else {
    t17 = $[45];
  }
  const [marketCapSeedData, setMarketCapSeedData] = useState(t17);
  const selectedChartWindow = useSelectedChartWindow(chartWindowIdentity, chartWindowSecs);
  const {
    tapeQuery: allTradesQuery,
    tapeRows: allTradesRows,
    viewerQuery: viewerTradesQuery,
    viewerRows: viewerTradeRows
  } = useMarketTradeStreams(buildMarketTradeStreamArgs({
    marketPubkey,
    tapeFrom: props.tradesFrom,
    viewerFrom: userTradesFrom,
    viewerWallet: authenticatedViewerPubkey
  }));
  useAutoPageTrades(viewerTradesQuery, authenticatedViewerPubkey !== undefined, "user-markers", marketPubkey);
  let t18;
  if ($[46] === Symbol.for("react.memo_cache_sentinel")) {
    t18 = ["eventId", "timestamp", "amount", "boosterPubkey"];
    $[46] = t18;
  } else {
    t18 = $[46];
  }
  let t19;
  if ($[47] !== marketPubkey) {
    t19 = {
      pubkey: marketPubkey,
      view: "events",
      fields: t18
    };
    $[47] = marketPubkey;
    $[48] = t19;
  } else {
    t19 = $[48];
  }
  const boostQuery = useGetMarketBoostEvents(t19);
  const v2Threshold = props.resolutionThreshold;
  const convictionMarket = {
    status: marketData?.status,
    resolvedAt: marketData?.resolvedAt,
    convictionUpdatedAt: marketData?.convictionUpdatedAt,
    outcomeCount
  };
  const outcomes = buildConnectedOutcomes(outcomeRows, convictionMarket);
  const userTrades = buildConnectedUserTrades(viewerTradeRows, authenticatedViewerPubkey);
  const boostEventMapping = mapBoostEventRows(boostQuery.rows);
  const boostEvents = boostEventMapping.events;
  useEffect(() => {
    if (boostEventMapping.invalidRowCount === 0) {
      return;
    }
    logger.warn("market.chart.boost_marker_rows_invalid", {
      marketPubkey,
      invalidRowCount: boostEventMapping.invalidRowCount
    });
  }, [boostEventMapping.invalidRowCount, marketPubkey]);
  let t20;
  let unavailableTapeLiquidityModelOutcomeKey;
  let unavailableTapeLiquidityModelRows;
  if ($[49] !== allTradesRows || $[50] !== marketPubkey) {
    unavailableTapeLiquidityModelRows = allTradesRows.filter(_temp16);
    const unavailableTapeLiquidityModelOutcomeIndices = [...new Set(unavailableTapeLiquidityModelRows.map(_temp17))].toSorted(_temp18);
    unavailableTapeLiquidityModelOutcomeKey = unavailableTapeLiquidityModelOutcomeIndices.join(",");
    bb0: {
      if (unavailableTapeLiquidityModelRows.length === 0) {
        t20 = null;
        break bb0;
      }
      const reasons = [...new Set(unavailableTapeLiquidityModelRows.map(_temp19))].toSorted();
      t20 = `${marketPubkey}|${unavailableTapeLiquidityModelOutcomeKey}|${reasons.join(",")}`;
    }
    $[49] = allTradesRows;
    $[50] = marketPubkey;
    $[51] = t20;
    $[52] = unavailableTapeLiquidityModelOutcomeKey;
    $[53] = unavailableTapeLiquidityModelRows;
  } else {
    t20 = $[51];
    unavailableTapeLiquidityModelOutcomeKey = $[52];
    unavailableTapeLiquidityModelRows = $[53];
  }
  const unavailableTapeLiquidityModelFingerprint = t20;
  const warnedTapeLiquidityModelFingerprint = useRef(null);
  let t21;
  let t22;
  if ($[54] !== marketPubkey || $[55] !== unavailableTapeLiquidityModelFingerprint || $[56] !== unavailableTapeLiquidityModelOutcomeKey || $[57] !== unavailableTapeLiquidityModelRows.length) {
    t21 = () => {
      if (unavailableTapeLiquidityModelFingerprint === null) {
        warnedTapeLiquidityModelFingerprint.current = null;
        return;
      }
      if (warnedTapeLiquidityModelFingerprint.current === unavailableTapeLiquidityModelFingerprint) {
        return;
      }
      warnedTapeLiquidityModelFingerprint.current = unavailableTapeLiquidityModelFingerprint;
      logger.warn("chart.trade_liquidity_model_unavailable", {
        marketPubkey,
        droppedTradeCount: unavailableTapeLiquidityModelRows.length,
        affectedOutcomeIndices: unavailableTapeLiquidityModelOutcomeKey.split(",").filter(_temp20).map(Number)
      });
    };
    t22 = [marketPubkey, unavailableTapeLiquidityModelFingerprint, unavailableTapeLiquidityModelOutcomeKey, unavailableTapeLiquidityModelRows.length];
    $[54] = marketPubkey;
    $[55] = unavailableTapeLiquidityModelFingerprint;
    $[56] = unavailableTapeLiquidityModelOutcomeKey;
    $[57] = unavailableTapeLiquidityModelRows.length;
    $[58] = t21;
    $[59] = t22;
  } else {
    t21 = $[58];
    t22 = $[59];
  }
  useEffect(t21, t22);
  const liveTradeCutoffMs = Date.now() - LIVE_WINDOW_SECONDS * 1000;
  const candleBucketMs = LIVE_CANDLE_BUCKET_SECONDS * 1000;
  const liveCandlePaging = interval === "live" && chartMode === "live" && renderMode === "candle";
  const liveTradePageCutoffMs = liveCandlePaging ? Math.floor(liveTradeCutoffMs / candleBucketMs) * candleBucketMs : liveTradeCutoffMs;
  const liveTradePagesPending = liveCandlePaging && allTradesQuery.isFetchingNextPage || allTradesQuery.hasNextPage && shouldPageLiveTradeTape(interval, allTradesRows, liveTradePageCutoffMs, liveCandlePaging, allTradesQuery.lastFetchedTimestamp);
  useAutoPageTrades(allTradesQuery, liveTradePagesPending, "live-tape", marketPubkey);
  const {
    marketCapLiveEnabled,
    liveCandleEnabled
  } = resolveConnectedLiveFlags({
    interval,
    chartMode,
    yAxisProperty,
    renderMode,
    readOnly
  });
  const candleClockEnabled = renderMode === "candle" && chartMode === "live" && !readOnly;
  const [liveCandleNowSec, setLiveCandleNowSec] = useState(_temp21);
  let t23;
  let t24;
  if ($[60] !== candleClockEnabled) {
    t23 = () => {
      if (!candleClockEnabled) {
        return;
      }
      const intervalId = setInterval(() => {
        setLiveCandleNowSec(Math.floor(Date.now() / 1000));
      }, 1000);
      return () => {
        clearInterval(intervalId);
      };
    };
    t24 = [candleClockEnabled];
    $[60] = candleClockEnabled;
    $[61] = t23;
    $[62] = t24;
  } else {
    t23 = $[61];
    t24 = $[62];
  }
  useEffect(t23, t24);
  const buildLiveChartData = trajectories => buildConnectedLiveChartData({
    liveCandleEnabled,
    liveCandleNowSec,
    yAxisProperty,
    allTradesRows,
    indexedOutcomeMintSupplyD6ByOutcome,
    initialOutcomeSupplyD6ByOutcome,
    virtualQuoteD6ByOutcome,
    outcomeCount,
    chartMode,
    marketCapLiveEnabled,
    liveStepLine,
    outcomeRows,
    currentLiquidityModelByOutcome,
    currentCpMarketCapD6ByOutcome,
    outcomes,
    marketCapSeedData
  }, trajectories);
  const selectedHistoryPublished = renderedOutcomeIndices.length > 0 && renderedOutcomeIndices.every(index_2 => chartLeafData[index_2]?.requestKey === chartDataRequestKey);
  if (selectedHistoryPublished && publishedTimeframe.chartDataRequestKey !== chartDataRequestKey) {
    setPublishedTimeframe(timeframe);
  }
  const retainedHistoryCompatible = !selectedHistoryPublished && publishedTimeframe.chartSeriesKey === chartSeriesKey && publishedTimeframe.interval === interval && publishedTimeframe.chartFrom === chartFrom && publishedTimeframe.chartTo === chartTo;
  const presentationHistoryRequestKey = retainedHistoryCompatible ? publishedTimeframe.chartDataRequestKey : undefined;
  const presentationChartLeafData = Object.fromEntries(renderedOutcomeIndices.map(index_3 => {
    const current = chartLeafData[index_3];
    const retained = retainedChartLeafData[index_3];
    const prefetched = retainedHistoryCompatible && retained?.requestKey === presentationHistoryRequestKey ? retained : undefined;
    return [index_3, current?.requestKey === chartDataRequestKey || prefetched === undefined ? current : {
      ...prefetched,
      requestKey: chartDataRequestKey
    }];
  }));
  let t25;
  bb1: {
    if (interval === "live") {
      t25 = EMPTY_CHART_DATA;
      break bb1;
    }
    const map = {};
    for (let i = 0; i < outcomeCount; i++) {
      const entry_1 = presentationChartLeafData[i];
      if (entry_1 === undefined || entry_1.requestKey !== chartDataRequestKey) {
        continue;
      }
      const data = entry_1.ohlcv !== undefined ? {
        line: entry_1.line,
        ohlcv: entry_1.ohlcv,
        ...(entry_1.leftEdgeValue === undefined ? {} : {
          leftEdgeValue: entry_1.leftEdgeValue
        }),
        ...(entry_1.bucketSeconds === undefined ? {} : {
          bucketSeconds: entry_1.bucketSeconds
        })
      } : {
        line: entry_1.line,
        ...(entry_1.lineTimeKind === undefined ? {} : {
          lineTimeKind: entry_1.lineTimeKind
        }),
        ...(entry_1.leftEdgeValue === undefined ? {} : {
          leftEdgeValue: entry_1.leftEdgeValue
        }),
        ...(entry_1.bucketSeconds === undefined ? {} : {
          bucketSeconds: entry_1.bucketSeconds
        })
      };
      if (!locked && interval === "all" && backendChartType === "line" && marketCreatedAtRaw !== undefined) {
        const initialValue = backendProperty === "twar" ? initialMarketShare(outcomeCount) : entry_1.leftEdgeValue;
        if (initialValue !== undefined) {
          data.initialPoint = {
            time: marketCreatedAtRaw,
            value: initialValue
          };
        }
      }
      map[i] = data;
    }
    t25 = map;
  }
  const selectedChartDataBase = t25;
  const terminalChartDataBase = clipTerminalChartDataAt(selectedChartDataBase, locked ? resolutionUnix : undefined, locked ? marketCreatedAtRaw : undefined);
  let t26;
  bb2: {
    if (terminalOpening === undefined) {
      t26 = terminalChartDataBase;
      break bb2;
    }
    const merged = {
      ...terminalChartDataBase
    };
    for (const outcomeIndex of renderedOutcomeIndices) {
      const history = merged[outcomeIndex];
      const opening_0 = terminalOpeningData[outcomeIndex];
      if (history === undefined || opening_0 === undefined || opening_0.requestKey !== terminalOpening.requestKey) {
        continue;
      }
      const clippedOpening = clipTerminalChartDataAt({
        [outcomeIndex]: opening_0
      }, locked ? resolutionUnix : undefined, locked ? marketCreatedAtRaw : undefined)[outcomeIndex];
      if (clippedOpening === undefined) {
        continue;
      }
      merged[outcomeIndex] = {
        ...history,
        line: mergeTerminalChartOpeningPoints(history.line, clippedOpening.line)
      };
    }
    t26 = merged;
  }
  const selectedChartData = t26;
  const {
    liveChartReady,
    targetChartReady
  } = resolveConnectedChartReadiness({
    interval,
    outcomeCount,
    renderedOutcomeIndices,
    marketCapLiveEnabled,
    marketCapSeedData,
    tradesPending: allTradesQuery.isPending || liveCandleEnabled && liveTradePagesPending,
    chartLeafData: presentationChartLeafData,
    chartDataRequestKey,
    terminalOpening,
    terminalOpeningData
  });
  const historyStartUnix = earliestChartPointUnix(selectedChartData);
  const selectedWindowSecs = resolveConnectedWindowSeconds({
    locked,
    anchorSec,
    marketCreatedAtRaw,
    historyStartUnix,
    chartWindowSecs,
    interval,
    selectedChartWindow,
    chartWindowIdentity
  });
  const timeframePresentation = useTimeframeChartPresentation({
    seriesKey: chartSeriesKey,
    requestKey: chartDataRequestKey,
    data: selectedChartData,
    targetReady: targetChartReady,
    windowSecs: selectedWindowSecs
  });
  const v2ChartData = resolveNullableChartData(timeframePresentation.data);
  useEffect(() => {
    if (!locked || !targetChartReady || marketCreatedAtRaw !== undefined || historyStartUnix !== undefined) {
      return;
    }
    logger.warn("chart.terminal_history_window_unavailable", {
      marketPubkey,
      resolutionUnix: resolutionUnix ?? null
    });
  }, [historyStartUnix, locked, marketCreatedAtRaw, marketPubkey, resolutionUnix, targetChartReady]);
  const consensusStopped = resolveConnectedConsensusDisplay({
    chartData: v2ChartData,
    threshold: v2Threshold,
    chartMode,
    resolutionUnix,
    winnerOutcomeIndex,
    outcomeRows
  });
  const missingSnapshotOutcomeIndices = consensusStopped.missingSnapshotOutcomeIndices?.join(",");
  useEffect(() => {
    if (!targetChartReady || !missingSnapshotOutcomeIndices) {
      return;
    }
    logger.warn("chart.terminal_consensus_snapshot_unavailable", {
      marketPubkey,
      resolutionUnix,
      outcomeIndices: missingSnapshotOutcomeIndices
    });
  }, [marketPubkey, missingSnapshotOutcomeIndices, resolutionUnix, targetChartReady]);
  const displayChartData = consensusStopped.chartData;
  const resolutionMarkerUnix = resolveConnectedResolutionMarker(chartMode, consensusStopped.stopTimestamp, resolutionUnix);
  const chartRightEdgeSec = anchorSec;
  const v2LiveValues = resolveConnectedLiveValues({
    locked,
    consensusStopTimestamp: consensusStopped.stopTimestamp,
    terminalValues: consensusStopped.terminalValues,
    chartMode,
    yAxisProperty,
    onchainTradeData,
    outcomeRows,
    convictionMarket,
    currentLiquidityModelByOutcome,
    currentCpMarketCapD6ByOutcome,
    marketCapSeedData
  });
  const renderChartData = resolveRenderableChartData(displayChartData);
  const selectedOutcomeMap = useChartSettingsStore(_temp22);
  const setSelectedOutcome = useChartSettingsStore(_temp23);
  const adaptiveBucketSeconds = displayChartData[selectedOutcomeMap[marketPubkey] ?? 0]?.bucketSeconds;
  const {
    focusedOutcomeIndex,
    marketChartMode,
    candleWidthSeconds
  } = resolveConnectedCandleModel({
    selectedOutcome: selectedOutcomeMap[marketPubkey],
    backendChartType,
    interval,
    adaptiveBucketSeconds
  });
  const v2OutcomeHues = useOutcomeColor(buildOutcomeColorInputs(outcomes));
  const v2OutcomesWithColor = buildConnectedOutcomesWithColor({
    outcomes,
    hues: v2OutcomeHues,
    seriesColorVar
  });
  const liveTradeIds = new Set(allTradesQuery.liveSinceMountIds);
  const colors = {};
  for (let index_4 = 0; index_4 < (outcomes ?? []).length; index_4 = index_4 + 1, index_4) {
    const outcome = (outcomes ?? [])[index_4];
    const hue = v2OutcomeHues[index_4]?.hue;
    if (outcome === undefined || hue === undefined) {
      continue;
    }
    const token = outcomeHueToStepVar(hue, 400);
    const variableName = token.match(/var\((--[^,)]+)/)?.[1];
    let literal = token;
    if (typeof window !== "undefined" && variableName !== undefined) {
      const resolved = getComputedStyle(document.documentElement).getPropertyValue(variableName).trim();
      if (resolved !== "") {
        literal = resolved;
      }
    }
    colors[outcome.index] = literal;
  }
  const outcomeColorByIndex = colors;
  const tradeTape = selectActiveTradeRows(allTradesRows, liveTradeIds).flatMap(trade_2 => {
    const usdcAmount = trade_2.usdcAmount;
    if (usdcAmount === undefined || usdcAmount === null) {
      return [];
    }
    const size = d6ToNum(String(usdcAmount));
    const side = resolveTradeSide(trade_2);
    if (size <= 0 || side === undefined) {
      return [];
    }
    const outcomeIndex_0 = Number(trade_2.outcomeIndex);
    const color = outcomeColorByIndex[outcomeIndex_0];
    const time = isoToUnix(trade_2.timestamp);
    const entry_2 = {
      id: trade_2.eventId,
      side,
      size,
      ...(time === undefined ? {} : {
        time
      }),
      label: `${side === "buy" ? "+" : "-"}${formatD6(String(usdcAmount), "compact")}`,
      ...(color === undefined ? {} : {
        color
      })
    };
    return [entry_2];
  });
  useEffect(() => {
    if (!debugMode) {
      return;
    }
    logger.info("[ConnectedChart] query params", {
      marketPubkey: marketPubkey.slice(0, 8),
      interval,
      backendInterval,
      yAxisProperty,
      renderMode,
      chartMode,
      backendProperty,
      backendChartType,
      outcomeCount,
      chartFrom: chartFrom ?? "none",
      chartTo: chartTo ?? "none",
      liveWindowSeconds: liveWindowSeconds ?? "fixed"
    });
  }, [debugMode, marketPubkey, interval, backendInterval, yAxisProperty, renderMode, chartMode, backendProperty, backendChartType, outcomeCount, chartFrom, chartTo, liveWindowSeconds]);
  const effectiveWindowSecs = timeframePresentation.windowSecs;
  const v2Trades = userTrades.flatMap(_temp24);
  const v2Boosts = boostEvents.map(_temp25);
  const t27 = opening?.interval === interval ? openingConviction : null;
  let t28;
  if ($[63] === Symbol.for("react.memo_cache_sentinel")) {
    t28 = () => setOpeningConviction(null);
    $[63] = t28;
  } else {
    t28 = $[63];
  }
  const t29 = resolveConnectedCandleNowSec({
    resolutionMarkerUnix,
    chartRightEdgeSec,
    liveCandleNowSec
  });
  let t30;
  if ($[64] !== backendChartType || $[65] !== backendInterval || $[66] !== backendProperty || $[67] !== breakevens || $[68] !== buildLiveChartData || $[69] !== candleWidthSeconds || $[70] !== chartDataRequestKey || $[71] !== chartFrom || $[72] !== chartMetricTransitionGroupKey || $[73] !== chartMode || $[74] !== chartRightEdgeSec || $[75] !== chartSeriesKey || $[76] !== chartTo || $[77] !== dataAvailableIntervals || $[78] !== effectiveWindowSecs || $[79] !== focusedOutcomeIndex || $[80] !== hiddenOutcomes || $[81] !== interval || $[82] !== liveChartReady || $[83] !== liveWindowSeconds || $[84] !== locked || $[85] !== marketCapLiveEnabled || $[86] !== marketChartMode || $[87] !== marketShareSamples || $[88] !== outcomeCount || $[89] !== outcomes || $[90] !== presentationHistoryRequestKey || $[91] !== renderChartData || $[92] !== renderedChartFrameStore || $[93] !== renderedOutcomeIndices || $[94] !== resolutionMarkerUnix || $[95] !== selectedWindowSecs || $[96] !== setChartInterval || $[97] !== setChartLeafData || $[98] !== setChartMode || $[99] !== setMarketCapSeedData || $[100] !== setNowMinuteMs || $[101] !== setRetainedChartLeafData || $[102] !== setSelectedOutcome || $[103] !== setTerminalOpeningData || $[104] !== setYAxisProperty || $[105] !== showBreakEven || $[106] !== t || $[107] !== t27 || $[108] !== t28 || $[109] !== t29 || $[110] !== targetBars || $[111] !== targetChartReady || $[112] !== terminalOpening || $[113] !== timeframeTransitionFrame || $[114] !== timeframeTransitionGeneration || $[115] !== toggleOutcome || $[116] !== tradeTape || $[117] !== v2Boosts || $[118] !== v2LiveValues || $[119] !== v2OutcomesWithColor || $[120] !== v2Threshold || $[121] !== v2Trades || $[122] !== yAxisProperty) {
    t30 = {
      t,
      interval,
      chartMode,
      yAxisProperty,
      renderedOutcomeIndices,
      backendProperty,
      backendChartType,
      backendInterval,
      chartFrom,
      chartTo,
      liveWindowSeconds,
      targetBars,
      marketShareSamples,
      openingConviction: t27,
      retireOpeningConviction: t28,
      onPlotWidthChange: setPlotWidth,
      chartSeriesKey,
      chartMetricTransitionGroupKey,
      chartDataRequestKey,
      setChartLeafData,
      presentationHistoryRequestKey,
      setRetainedChartLeafData,
      terminalOpening,
      setTerminalOpeningData,
      setNowMinuteMs,
      marketCapLiveEnabled,
      setMarketCapSeedData,
      outcomeCount,
      liveChartReady,
      buildLiveChartData,
      renderChartData,
      renderedChartFrameStore,
      timeframeTransitionFrame,
      timeframeTransitionGeneration,
      targetChartReady,
      outcomes,
      breakevens,
      showBreakEven,
      v2Trades,
      v2Boosts,
      tradeTape,
      hiddenOutcomes,
      v2Threshold,
      resolutionMarkerUnix,
      chartRightEdgeSec,
      candleNowSec: t29,
      v2LiveValues,
      marketChartMode,
      focusedOutcomeIndex,
      candleWidthSeconds,
      requestedWindowSecs: selectedWindowSecs,
      effectiveWindowSecs,
      dataAvailableIntervals,
      locked,
      v2OutcomesWithColor,
      setChartMode,
      setChartInterval,
      setYAxisProperty,
      setSelectedOutcome,
      toggleOutcome
    };
    $[64] = backendChartType;
    $[65] = backendInterval;
    $[66] = backendProperty;
    $[67] = breakevens;
    $[68] = buildLiveChartData;
    $[69] = candleWidthSeconds;
    $[70] = chartDataRequestKey;
    $[71] = chartFrom;
    $[72] = chartMetricTransitionGroupKey;
    $[73] = chartMode;
    $[74] = chartRightEdgeSec;
    $[75] = chartSeriesKey;
    $[76] = chartTo;
    $[77] = dataAvailableIntervals;
    $[78] = effectiveWindowSecs;
    $[79] = focusedOutcomeIndex;
    $[80] = hiddenOutcomes;
    $[81] = interval;
    $[82] = liveChartReady;
    $[83] = liveWindowSeconds;
    $[84] = locked;
    $[85] = marketCapLiveEnabled;
    $[86] = marketChartMode;
    $[87] = marketShareSamples;
    $[88] = outcomeCount;
    $[89] = outcomes;
    $[90] = presentationHistoryRequestKey;
    $[91] = renderChartData;
    $[92] = renderedChartFrameStore;
    $[93] = renderedOutcomeIndices;
    $[94] = resolutionMarkerUnix;
    $[95] = selectedWindowSecs;
    $[96] = setChartInterval;
    $[97] = setChartLeafData;
    $[98] = setChartMode;
    $[99] = setMarketCapSeedData;
    $[100] = setNowMinuteMs;
    $[101] = setRetainedChartLeafData;
    $[102] = setSelectedOutcome;
    $[103] = setTerminalOpeningData;
    $[104] = setYAxisProperty;
    $[105] = showBreakEven;
    $[106] = t;
    $[107] = t27;
    $[108] = t28;
    $[109] = t29;
    $[110] = targetBars;
    $[111] = targetChartReady;
    $[112] = terminalOpening;
    $[113] = timeframeTransitionFrame;
    $[114] = timeframeTransitionGeneration;
    $[115] = toggleOutcome;
    $[116] = tradeTape;
    $[117] = v2Boosts;
    $[118] = v2LiveValues;
    $[119] = v2OutcomesWithColor;
    $[120] = v2Threshold;
    $[121] = v2Trades;
    $[122] = yAxisProperty;
    $[123] = t30;
  } else {
    t30 = $[123];
  }
  return t30;
}
function _temp25(boost) {
  return {
    id: boost.id,
    time: Number(boost.time),
    quoteAmount: Number(boost.amountD6) / D6_SCALE
  };
}
function _temp24(trade_3) {
  return trade_3.side === undefined ? [] : [{
    id: trade_3.signature,
    time: Number(trade_3.timestamp),
    outcomeIndex: trade_3.outcomeIndex,
    side: trade_3.side,
    quoteAmount: trade_3.usdcAmount
  }];
}
function _temp23(s_11) {
  return s_11.setSelectedOutcome;
}
function _temp22(s_10) {
  return s_10.selectedOutcome;
}
function _temp21() {
  return Math.floor(Date.now() / 1000);
}
function _temp20(value) {
  return value !== "";
}
function _temp19(trade_1) {
  return trade_1.liquidityModel === null || trade_1.liquidityModel === undefined ? "missing" : "malformed";
}
function _temp18(left, right) {
  return left - right;
}
function _temp17(trade_0) {
  return Number(trade_0.outcomeIndex);
}
function _temp16(trade) {
  return parseTradeLiquidityModel(trade.liquidityModel) === undefined;
}
function _temp15(generation) {
  return generation + 1;
}
function _temp14() {
  return createTimeframeRenderedFrameStore();
}
function _temp13(s_9) {
  return s_9.setYAxisProperty;
}
function _temp12(s_8) {
  return s_8.setInterval;
}
function _temp11(s_7) {
  return s_7.setChartMode;
}
function _temp10(s_6) {
  return s_6.renderMode;
}
function _temp9(s_5) {
  return s_5.yAxisProperty;
}
function _temp8(s_4) {
  return s_4.chartMode;
}
function _temp7(s_3) {
  return s_3.interval;
}
function _temp6(s_2) {
  return s_2.toggleOutcome;
}
function _temp5(s_0) {
  return s_0.showBreakEven;
}
interface ConnectedMarketChartProps {
  readonly model: ConnectedChartModel;
  readonly marketPubkey: string;
  readonly seriesColorVar: string | undefined;
  readonly chartHeight: number;
  readonly mobileChartHeight: number;
}
interface ConnectedMarketChartOwnProps extends ConnectedMarketChartProps {
  readonly marketData: GetMarketRow | undefined;
  readonly resolutionLanded: boolean;
}
function ConnectedMarketChart(props) {
  const $ = _c(24);
  const network = useActiveNetwork();
  const paintedDisplayStore = usePaintedMarketShareDisplayStore();
  const {
    enabled,
    authoritativeMode
  } = useAuthoritativeMarketSharePresentation();
  const sessionBaseKey = `${network}:${props.marketPubkey}`;
  const t0 = `${props.model.interval}:${props.model.chartMode}:${props.model.marketChartMode}`;
  const t1 = authoritativeMode ? true : props.model.targetChartReady;
  let t2;
  if ($[0] !== authoritativeMode || $[1] !== enabled || $[2] !== props.model || $[3] !== sessionBaseKey || $[4] !== t0 || $[5] !== t1) {
    t2 = {
      authoritativeMode,
      enabled,
      locked: props.model.locked,
      sessionBaseKey,
      lockedControlKey: t0,
      targetChartReady: t1,
      liveValue: props.model
    };
    $[0] = authoritativeMode;
    $[1] = enabled;
    $[2] = props.model;
    $[3] = sessionBaseKey;
    $[4] = t0;
    $[5] = t1;
    $[6] = t2;
  } else {
    t2 = $[6];
  }
  const terminalHandoff = useAuthoritativeTerminalHandoff(t2);
  const hasActiveSession = terminalHandoff.sessionGeneration !== undefined;
  let t3;
  if ($[7] !== network || $[8] !== props.marketPubkey || $[9] !== terminalHandoff.sessionGeneration) {
    t3 = terminalHandoff.sessionGeneration === undefined ? undefined : {
      network,
      marketPubkey: props.marketPubkey,
      generation: terminalHandoff.sessionGeneration
    };
    $[7] = network;
    $[8] = props.marketPubkey;
    $[9] = terminalHandoff.sessionGeneration;
    $[10] = t3;
  } else {
    t3 = $[10];
  }
  const sessionIdentity = t3;
  let t4;
  let t5;
  if ($[11] !== hasActiveSession || $[12] !== paintedDisplayStore) {
    t4 = () => {
      if (hasActiveSession) {
        return;
      }
      paintedDisplayStore.reset();
    };
    t5 = [paintedDisplayStore, hasActiveSession];
    $[11] = hasActiveSession;
    $[12] = paintedDisplayStore;
    $[13] = t4;
    $[14] = t5;
  } else {
    t4 = $[13];
    t5 = $[14];
  }
  useLayoutEffect(t4, t5);
  let t6;
  if ($[15] !== authoritativeMode || $[16] !== paintedDisplayStore || $[17] !== props || $[18] !== sessionIdentity || $[19] !== terminalHandoff.displayValue || $[20] !== terminalHandoff.holdingTerminal || $[21] !== terminalHandoff.onTerminalPainted || $[22] !== terminalHandoff.sessionKey) {
    t6 = authoritativeMode || terminalHandoff.holdingTerminal ? <ConnectedMarketShareChart {...props} model={terminalHandoff.displayValue} paintedDisplayStore={paintedDisplayStore} sessionIdentity={sessionIdentity} {...terminalHandoff.sessionKey === undefined ? {} : {
      terminalSessionKey: terminalHandoff.sessionKey,
      onTerminalPainted: terminalHandoff.onTerminalPainted
    }} /> : <ConnectedPriceMarketCapChart {...props} />;
    $[15] = authoritativeMode;
    $[16] = paintedDisplayStore;
    $[17] = props;
    $[18] = sessionIdentity;
    $[19] = terminalHandoff.displayValue;
    $[20] = terminalHandoff.holdingTerminal;
    $[21] = terminalHandoff.onTerminalPainted;
    $[22] = terminalHandoff.sessionKey;
    $[23] = t6;
  } else {
    t6 = $[23];
  }
  return t6;
}
interface ConnectedMarketShareChartProps extends ConnectedMarketChartOwnProps {
  readonly paintedDisplayStore: PaintedMarketShareDisplayStore;
  readonly sessionIdentity: PaintedMarketShareSessionIdentity | undefined;
  readonly terminalSessionKey?: string;
  readonly onTerminalPainted?: (ack: AuthoritativeTerminalPaintAck) => void;
}
function ConnectedMarketShareChart(props) {
  const $ = _c(27);
  const clockSnapshot = useServerClock();
  const marketConfig = useMarketConfig(props.marketData?.marketConfig);
  let t0;
  if ($[0] !== marketConfig || $[1] !== props.model.outcomeCount) {
    t0 = deriveMarketResolutionThresholdPolicy({
      config: marketConfig,
      outcomeCount: props.model.outcomeCount
    });
    $[0] = marketConfig;
    $[1] = props.model.outcomeCount;
    $[2] = t0;
  } else {
    t0 = $[2];
  }
  const thresholdPolicy = t0;
  const [frameSource] = useState(createMarketShareLivelineFrameSource);
  const readyClock = clockSnapshot.status === "ready" ? clockSnapshot.clock : null;
  const tradingStartedAt = props.marketData?.tradingStartedAt ?? null;
  const sessionNetwork = props.sessionIdentity?.network;
  const sessionMarketPubkey = props.sessionIdentity?.marketPubkey;
  const sessionGeneration = props.sessionIdentity?.generation;
  let t1;
  let t2;
  if ($[3] !== props.paintedDisplayStore || $[4] !== sessionGeneration || $[5] !== sessionMarketPubkey || $[6] !== sessionNetwork) {
    t1 = () => {
      if (sessionNetwork === undefined || sessionMarketPubkey === undefined || sessionGeneration === undefined) {
        return;
      }
      const identity = {
        network: sessionNetwork,
        marketPubkey: sessionMarketPubkey,
        generation: sessionGeneration
      };
      props.paintedDisplayStore.activate(identity);
      return () => props.paintedDisplayStore.pause(identity);
    };
    t2 = [props.paintedDisplayStore, sessionNetwork, sessionMarketPubkey, sessionGeneration];
    $[3] = props.paintedDisplayStore;
    $[4] = sessionGeneration;
    $[5] = sessionMarketPubkey;
    $[6] = sessionNetwork;
    $[7] = t1;
    $[8] = t2;
  } else {
    t1 = $[7];
    t2 = $[8];
  }
  useLayoutEffect(t1, t2);
  if (marketConfig === undefined || tradingStartedAt === null) {
    const t3 = clockSnapshot.status === "unavailable";
    let t4;
    if ($[9] !== frameSource || $[10] !== t3) {
      t4 = {
        frameSource,
        ready: false,
        settled: t3
      };
      $[9] = frameSource;
      $[10] = t3;
      $[11] = t4;
    } else {
      t4 = $[11];
    }
    let t5;
    if ($[12] !== props.chartHeight || $[13] !== props.marketPubkey || $[14] !== props.mobileChartHeight || $[15] !== props.model || $[16] !== props.seriesColorVar || $[17] !== t4) {
      t5 = <ConnectedMarketChartFrame model={props.model} marketPubkey={props.marketPubkey} trajectories={EMPTY_LIVE_TRAJECTORIES} authoritative={t4} seriesColorVar={props.seriesColorVar} chartHeight={props.chartHeight} mobileChartHeight={props.mobileChartHeight} />;
      $[12] = props.chartHeight;
      $[13] = props.marketPubkey;
      $[14] = props.mobileChartHeight;
      $[15] = props.model;
      $[16] = props.seriesColorVar;
      $[17] = t4;
      $[18] = t5;
    } else {
      t5 = $[18];
    }
    return t5;
  }
  const t3 = clockSnapshot.status === "unavailable";
  let t4;
  if ($[19] !== frameSource || $[20] !== marketConfig || $[21] !== props || $[22] !== readyClock || $[23] !== t3 || $[24] !== thresholdPolicy || $[25] !== tradingStartedAt) {
    t4 = <ConnectedMarketShareChartQuery {...props} frameSource={frameSource} clock={readyClock} clockUnavailable={t3} tradingStartedAt={tradingStartedAt} retentionPerSecondPpb={marketConfig.convictionRetentionPerSecondPpb} marketConfig={marketConfig} thresholdPolicy={thresholdPolicy} />;
    $[19] = frameSource;
    $[20] = marketConfig;
    $[21] = props;
    $[22] = readyClock;
    $[23] = t3;
    $[24] = thresholdPolicy;
    $[25] = tradingStartedAt;
    $[26] = t4;
  } else {
    t4 = $[26];
  }
  return t4;
}
interface ConnectedMarketShareChartQueryProps extends ConnectedMarketShareChartProps {
  readonly frameSource: MarketShareLivelineFrameSource;
  readonly clock: ReturnType<typeof useServerClock>['clock'];
  readonly clockUnavailable: boolean;
  readonly tradingStartedAt: string;
  readonly retentionPerSecondPpb: string;
  readonly marketConfig: NonNullable<ReturnType<typeof useMarketConfig>>;
  readonly thresholdPolicy: ReturnType<typeof deriveMarketResolutionThresholdPolicy>;
}
function ConnectedMarketShareChartQuery(props) {
  const $ = _c(84);
  let from;
  let head;
  let t0;
  let terminalTo;
  let thresholdSeries;
  if ($[0] !== props.marketData || $[1] !== props.model.chartRightEdgeSec || $[2] !== props.model.interval || $[3] !== props.model.locked || $[4] !== props.model.marketShareSamples || $[5] !== props.model.requestedWindowSecs || $[6] !== props.thresholdPolicy || $[7] !== props.tradingStartedAt) {
    head = new Date(props.model.chartRightEdgeSec * 1000).toISOString();
    from = resolveConvictionChartStart({
      tradingStartedAt: props.tradingStartedAt,
      head,
      ...(props.model.interval === "all" ? {} : {
        lookbackSeconds: props.model.requestedWindowSecs
      })
    });
    terminalTo = props.model.locked && props.marketData?.resolvedAt ? convictionChartTerminalTo(props.marketData.resolvedAt) : undefined;
    const t1 = props.marketData?.resolvedAt ?? null;
    const t2 = Date.parse(from) / 1000;
    const t3 = Date.parse(terminalTo ?? head) / 1000;
    let t4;
    if ($[13] !== props.thresholdPolicy || $[14] !== props.tradingStartedAt || $[15] !== t1 || $[16] !== t2 || $[17] !== t3) {
      t4 = consensusThresholdSeries({
        policy: props.thresholdPolicy,
        tradingStartedAt: props.tradingStartedAt,
        resolvedAt: t1,
        fromSeconds: t2,
        toSeconds: t3,
        interpolation: CONSENSUS_THRESHOLD_INTERPOLATION
      });
      $[13] = props.thresholdPolicy;
      $[14] = props.tradingStartedAt;
      $[15] = t1;
      $[16] = t2;
      $[17] = t3;
      $[18] = t4;
    } else {
      t4 = $[18];
    }
    thresholdSeries = t4;
    t0 = selectConvictionChartInterval(from, terminalTo ?? new Date(Date.parse(head) + 1).toISOString(), props.model.marketShareSamples);
    $[0] = props.marketData;
    $[1] = props.model.chartRightEdgeSec;
    $[2] = props.model.interval;
    $[3] = props.model.locked;
    $[4] = props.model.marketShareSamples;
    $[5] = props.model.requestedWindowSecs;
    $[6] = props.thresholdPolicy;
    $[7] = props.tradingStartedAt;
    $[8] = from;
    $[9] = head;
    $[10] = t0;
    $[11] = terminalTo;
    $[12] = thresholdSeries;
  } else {
    from = $[8];
    head = $[9];
    t0 = $[10];
    terminalTo = $[11];
    thresholdSeries = $[12];
  }
  const interval = t0;
  let t1;
  if ($[19] !== terminalTo) {
    t1 = terminalTo === undefined ? {} : {
      to: terminalTo
    };
    $[19] = terminalTo;
    $[20] = t1;
  } else {
    t1 = $[20];
  }
  let t2;
  if ($[21] !== from || $[22] !== head || $[23] !== interval || $[24] !== props.marketPubkey || $[25] !== props.model.interval || $[26] !== props.model.marketShareSamples || $[27] !== t1) {
    t2 = {
      pubkey: props.marketPubkey,
      selectionKey: props.model.interval,
      interval,
      from,
      head,
      limit: props.model.marketShareSamples,
      ...t1
    };
    $[21] = from;
    $[22] = head;
    $[23] = interval;
    $[24] = props.marketPubkey;
    $[25] = props.model.interval;
    $[26] = props.model.marketShareSamples;
    $[27] = t1;
    $[28] = t2;
  } else {
    t2 = $[28];
  }
  const query = useAuthoritativeConvictionChart(t2);
  const previousSnapshot = useRef(null);
  const previousSource = useRef(null);
  const [retainedMarket, setRetainedMarket] = useState(null);
  const requestKey = `${interval}:${from}:${terminalTo ?? "latest"}:${props.model.marketShareSamples}`;
  const snapshot = useConvictionOpeningSnapshot(query.data, props.model.openingConviction, props.model.retireOpeningConviction);
  const resolvedAt = props.marketData?.resolvedAt;
  let t3;
  let t4;
  if ($[29] !== props.clock || $[30] !== props.frameSource || $[31] !== props.marketConfig || $[32] !== props.marketData || $[33] !== props.marketPubkey || $[34] !== props.model.outcomeCount || $[35] !== props.onTerminalPainted || $[36] !== props.paintedDisplayStore || $[37] !== props.retentionPerSecondPpb || $[38] !== props.sessionIdentity || $[39] !== props.terminalSessionKey || $[40] !== props.thresholdPolicy || $[41] !== props.tradingStartedAt || $[42] !== query.data || $[43] !== query.error || $[44] !== requestKey || $[45] !== resolvedAt || $[46] !== snapshot) {
    t3 = () => {
      const geometryInput = {
        tradingStartedAt: props.tradingStartedAt,
        outcomeCount: props.model.outcomeCount,
        retentionPerSecondPpb: props.retentionPerSecondPpb,
        ...(resolvedAt === undefined ? {} : {
          resolvedAt
        })
      };
      const prior = previousSnapshot.current;
      const snapshotAffectedAtMs = snapshot === null || prior === null || prior.requestKey !== requestKey ? null : earliestChangedConvictionChartTime(prior.snapshot, snapshot);
      const terminalAffectedAtMs = prior !== null && prior.resolvedAt !== resolvedAt && resolvedAt !== null && resolvedAt !== undefined ? Date.parse(resolvedAt) : null;
      const chart = snapshot === null ? null : buildSparseConvictionChart({
        snapshot,
        ...geometryInput
      });
      const source = chart === null || snapshot === null ? query.error === null && previousSource.current?.marketPubkey === props.marketPubkey ? previousSource.current.source : null : {
        chart,
        geometryRevision: convictionChartGeometryRevision(snapshot, geometryInput),
        earliestAffectedAtMs: earliestFiniteTime(snapshotAffectedAtMs, terminalAffectedAtMs)
      };
      setRetainedMarket(source === null ? null : props.marketPubkey);
      props.frameSource.update({
        source,
        authoritative: query.data != null,
        clock: props.clock,
        degraded: query.error !== null,
        ...(props.sessionIdentity === undefined ? {} : {
          paintedDisplay: {
            store: props.paintedDisplayStore,
            identity: props.sessionIdentity
          }
        }),
        ...(props.terminalSessionKey === undefined || props.onTerminalPainted === undefined ? {} : {
          terminalSession: {
            key: props.terminalSessionKey,
            onPainted: props.onTerminalPainted
          }
        }),
        ...(props.marketData === undefined ? {} : {
          threshold: {
            config: props.marketConfig,
            policy: props.thresholdPolicy,
            status: props.marketData.status,
            tradingStartedAt: props.marketData.tradingStartedAt,
            resolvedAt: props.marketData.resolvedAt
          }
        })
      });
      if (snapshot !== null) {
        if (source !== null) {
          previousSource.current = {
            marketPubkey: props.marketPubkey,
            source
          };
        }
        previousSnapshot.current = {
          requestKey,
          snapshot,
          resolvedAt: props.marketData?.resolvedAt
        };
      }
    };
    t4 = [props.frameSource, props.marketPubkey, props.model.outcomeCount, props.marketData, props.marketConfig, props.paintedDisplayStore, props.sessionIdentity, props.clock, query.error, query.data, props.onTerminalPainted, props.terminalSessionKey, props.thresholdPolicy, props.tradingStartedAt, props.retentionPerSecondPpb, requestKey, resolvedAt, snapshot];
    $[29] = props.clock;
    $[30] = props.frameSource;
    $[31] = props.marketConfig;
    $[32] = props.marketData;
    $[33] = props.marketPubkey;
    $[34] = props.model.outcomeCount;
    $[35] = props.onTerminalPainted;
    $[36] = props.paintedDisplayStore;
    $[37] = props.retentionPerSecondPpb;
    $[38] = props.sessionIdentity;
    $[39] = props.terminalSessionKey;
    $[40] = props.thresholdPolicy;
    $[41] = props.tradingStartedAt;
    $[42] = query.data;
    $[43] = query.error;
    $[44] = requestKey;
    $[45] = resolvedAt;
    $[46] = snapshot;
    $[47] = t3;
    $[48] = t4;
  } else {
    t3 = $[47];
    t4 = $[48];
  }
  useLayoutEffect(t3, t4);
  let t5;
  let t6;
  if ($[49] !== from || $[50] !== interval || $[51] !== props.marketPubkey || $[52] !== query.error || $[53] !== terminalTo) {
    t5 = () => {
      if (query.error === null) {
        return;
      }
      logger.warn("chart.conviction_history_load_failed", {
        marketPubkey: props.marketPubkey,
        interval,
        from,
        to: terminalTo ?? null
      });
    };
    t6 = [from, interval, props.marketPubkey, query.error, terminalTo];
    $[49] = from;
    $[50] = interval;
    $[51] = props.marketPubkey;
    $[52] = query.error;
    $[53] = terminalTo;
    $[54] = t5;
    $[55] = t6;
  } else {
    t5 = $[54];
    t6 = $[55];
  }
  useEffect(t5, t6);
  let t7;
  let t8;
  if ($[56] !== props.paintedDisplayStore || $[57] !== props.sessionIdentity || $[58] !== query.error) {
    t7 = () => {
      const identity = props.sessionIdentity;
      if (identity === undefined || query.error === null) {
        return;
      }
      props.paintedDisplayStore.fail(identity);
    };
    t8 = [props.paintedDisplayStore, props.sessionIdentity, query.error];
    $[56] = props.paintedDisplayStore;
    $[57] = props.sessionIdentity;
    $[58] = query.error;
    $[59] = t7;
    $[60] = t8;
  } else {
    t7 = $[59];
    t8 = $[60];
  }
  useLayoutEffect(t7, t8);
  const ready = snapshot !== null && props.clock !== null;
  const displayReady = ready || retainedMarket === props.marketPubkey && props.clock !== null;
  const settled = query.error !== null || props.clockUnavailable;
  let t9;
  if ($[61] !== props.model.chartSeriesKey || $[62] !== props.model.requestedWindowSecs || $[63] !== ready || $[64] !== requestKey || $[65] !== snapshot) {
    t9 = {
      seriesKey: props.model.chartSeriesKey,
      requestKey,
      data: snapshot,
      targetReady: ready,
      windowSecs: props.model.requestedWindowSecs
    };
    $[61] = props.model.chartSeriesKey;
    $[62] = props.model.requestedWindowSecs;
    $[63] = ready;
    $[64] = requestKey;
    $[65] = snapshot;
    $[66] = t9;
  } else {
    t9 = $[66];
  }
  const timeframePresentation = useTimeframeChartPresentation(t9);
  let t10;
  if ($[67] !== props.model || $[68] !== timeframePresentation.windowSecs) {
    t10 = {
      ...props.model,
      effectiveWindowSecs: timeframePresentation.windowSecs
    };
    $[67] = props.model;
    $[68] = timeframePresentation.windowSecs;
    $[69] = t10;
  } else {
    t10 = $[69];
  }
  let t11;
  if ($[70] !== displayReady || $[71] !== props.frameSource || $[72] !== settled) {
    t11 = {
      frameSource: props.frameSource,
      ready: displayReady,
      settled
    };
    $[70] = displayReady;
    $[71] = props.frameSource;
    $[72] = settled;
    $[73] = t11;
  } else {
    t11 = $[73];
  }
  let t12;
  if ($[74] !== thresholdSeries) {
    t12 = thresholdSeries === null ? {} : {
      thresholdSeries
    };
    $[74] = thresholdSeries;
    $[75] = t12;
  } else {
    t12 = $[75];
  }
  let t13;
  if ($[76] !== props.chartHeight || $[77] !== props.marketPubkey || $[78] !== props.mobileChartHeight || $[79] !== props.seriesColorVar || $[80] !== t10 || $[81] !== t11 || $[82] !== t12) {
    t13 = <ConnectedMarketChartFrame model={t10} marketPubkey={props.marketPubkey} trajectories={EMPTY_LIVE_TRAJECTORIES} authoritative={t11} {...t12} seriesColorVar={props.seriesColorVar} chartHeight={props.chartHeight} mobileChartHeight={props.mobileChartHeight} />;
    $[76] = props.chartHeight;
    $[77] = props.marketPubkey;
    $[78] = props.mobileChartHeight;
    $[79] = props.seriesColorVar;
    $[80] = t10;
    $[81] = t11;
    $[82] = t12;
    $[83] = t13;
  } else {
    t13 = $[83];
  }
  return t13;
}
function earliestFiniteTime(left: number | null, right: number | null): number | null {
  const times = [left, right].filter((time): time is number => time !== null && Number.isFinite(time));
  return times.length === 0 ? null : Math.min(...times);
}
function ConnectedPriceMarketCapChart(t0) {
  const $ = _c(26);
  const {
    model: m,
    marketData,
    resolutionLanded,
    marketPubkey,
    seriesColorVar,
    chartHeight,
    mobileChartHeight
  } = t0;
  let t1;
  if ($[0] !== marketPubkey) {
    t1 = {
      pubkey: marketPubkey
    };
    $[0] = marketPubkey;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const liveRows = useGetMarketField(t1, "outcomes");
  const heldRows = useHeldWhile(liveRows ?? marketData?.outcomes, resolutionLanded);
  let t2;
  if ($[2] !== heldRows || $[3] !== m.locked || $[4] !== m.outcomeCount || $[5] !== m.outcomes || $[6] !== marketData?.convictionUpdatedAt || $[7] !== marketData?.resolvedAt || $[8] !== marketData?.status) {
    t2 = !m.locked && heldRows !== undefined ? buildConnectedOutcomes(heldRows, {
      status: marketData?.status,
      resolvedAt: marketData?.resolvedAt,
      convictionUpdatedAt: marketData?.convictionUpdatedAt,
      outcomeCount: m.outcomeCount
    }) : m.outcomes;
    $[2] = heldRows;
    $[3] = m.locked;
    $[4] = m.outcomeCount;
    $[5] = m.outcomes;
    $[6] = marketData?.convictionUpdatedAt;
    $[7] = marketData?.resolvedAt;
    $[8] = marketData?.status;
    $[9] = t2;
  } else {
    t2 = $[9];
  }
  const currentOutcomes = t2;
  let t3;
  if ($[10] !== currentOutcomes || $[11] !== m) {
    t3 = {
      ...m,
      outcomes: currentOutcomes
    };
    $[10] = currentOutcomes;
    $[11] = m;
    $[12] = t3;
  } else {
    t3 = $[12];
  }
  const liveModel = t3;
  let t4;
  if ($[13] !== currentOutcomes) {
    t4 = currentOutcomes ?? [];
    $[13] = currentOutcomes;
    $[14] = t4;
  } else {
    t4 = $[14];
  }
  const t5 = m.interval === "live" && !m.locked;
  const t6 = `${marketPubkey}|${m.interval}`;
  let t7;
  if ($[15] !== chartHeight || $[16] !== liveModel || $[17] !== marketPubkey || $[18] !== mobileChartHeight || $[19] !== seriesColorVar) {
    t7 = trajectories => <ConnectedMarketChartFrame model={liveModel} marketPubkey={marketPubkey} trajectories={trajectories} seriesColorVar={seriesColorVar} chartHeight={chartHeight} mobileChartHeight={mobileChartHeight} />;
    $[15] = chartHeight;
    $[16] = liveModel;
    $[17] = marketPubkey;
    $[18] = mobileChartHeight;
    $[19] = seriesColorVar;
    $[20] = t7;
  } else {
    t7 = $[20];
  }
  let t8;
  if ($[21] !== t4 || $[22] !== t5 || $[23] !== t6 || $[24] !== t7) {
    t8 = <LiveMarketCapTrajectoryBoundary outcomes={t4} enabled={t5} windowSecs={LIVE_WINDOW_SECONDS} resetKey={t6}>{t7}</LiveMarketCapTrajectoryBoundary>;
    $[21] = t4;
    $[22] = t5;
    $[23] = t6;
    $[24] = t7;
    $[25] = t8;
  } else {
    t8 = $[25];
  }
  return t8;
}
interface ConnectedMarketChartFrameProps extends ConnectedMarketChartProps {
  readonly trajectories: LiveMarketCapTrajectories;
  readonly thresholdSeries?: ReadonlyArray<LivelinePoint> | undefined;
  readonly authoritative?: {
    readonly frameSource: MarketShareLivelineFrameSource;
    readonly ready: boolean;
    readonly settled: boolean;
  };
}
function resolveConnectedTargetFrame(m: ConnectedChartModel, trajectories: LiveMarketCapTrajectories, marketPubkey: string, authoritative: ConnectedMarketChartFrameProps['authoritative']): {
  readonly frame: ConnectedChartRenderedFrame;
  readonly ready: boolean;
} {
  const data = authoritative === undefined ? m.interval === 'live' && m.liveChartReady ? m.buildLiveChartData(trajectories) : m.renderChartData ?? {} : Object.fromEntries(m.renderedOutcomeIndices.map(index => [index, {
    line: []
  }]));
  const policy = resolveConnectedChartFramePolicy({
    interval: m.interval,
    requestReady: m.targetChartReady,
    authoritativeReady: false,
    data
  });
  return {
    frame: {
      seriesKey: m.chartSeriesKey,
      metricTransitionGroupKey: m.chartMetricTransitionGroupKey,
      lifecycleHandoff: {
        key: [marketPubkey, m.backendProperty, m.backendChartType, m.renderedOutcomeIndices.join(',')].join('|'),
        locked: m.locked
      },
      data,
      windowSecs: m.effectiveWindowSecs,
      chartMode: m.chartMode,
      yAxisProperty: m.yAxisProperty,
      liveValues: authoritative === undefined ? m.v2LiveValues : undefined,
      authoritativeFrameSource: authoritative?.frameSource,
      lineCurve: m.interval === 'live' ? 'linear' : m.chartMode === 'consensus' ? 'rounded-linear' : 'linear'
    },
    ready: authoritative === undefined ? policy.ready : authoritative.ready || authoritative.settled
  };
}
function resolveConnectedRenderedFrame(args: {
  readonly targetFrame: ConnectedChartRenderedFrame;
  readonly targetReady: boolean;
  readonly capturedFrame: TimeframeRenderedFrame<Record<number, MarketChartData>> | null;
  readonly viewportTransitionComplete: boolean;
}): ConnectedChartRenderedFrame | null {
  const resolved = resolveTimeframeRenderedFrame({
    target: args.targetFrame,
    targetReady: args.targetReady,
    capturedFrame: args.capturedFrame,
    viewportTransitionComplete: args.viewportTransitionComplete
  });
  if (resolved === null) return null;
  if (resolved === args.targetFrame) return args.targetFrame;
  return {
    ...resolved,
    metricTransitionGroupKey: args.targetFrame.metricTransitionGroupKey,
    lifecycleHandoff: args.targetFrame.lifecycleHandoff,
    chartMode: args.targetFrame.chartMode,
    yAxisProperty: args.targetFrame.yAxisProperty,
    liveValues: args.targetFrame.liveValues,
    authoritativeFrameSource: args.targetFrame.authoritativeFrameSource
  };
}
function ConnectedMarketChartFrame(t0) {
  const $ = _c(33);
  const {
    model: m,
    marketPubkey,
    trajectories,
    seriesColorVar,
    chartHeight,
    mobileChartHeight,
    authoritative,
    thresholdSeries
  } = t0;
  const [completedTransitionGeneration, setCompletedTransitionGeneration] = useState(null);
  const [completedMetricSeriesKey, setCompletedMetricSeriesKey] = useState(null);
  const reduceMotion = useReducedMotion();
  let handleWindowTransitionComplete;
  let renderedFrame;
  let t1;
  let targetFrame;
  let targetReady;
  if ($[0] !== authoritative || $[1] !== completedMetricSeriesKey || $[2] !== completedTransitionGeneration || $[3] !== m || $[4] !== marketPubkey || $[5] !== trajectories) {
    const target = resolveConnectedTargetFrame(m, trajectories, marketPubkey, authoritative);
    targetFrame = target.frame;
    targetReady = target.ready;
    const retainOutgoingDataDuringViewportTransition = targetReady && m.timeframeTransitionFrame?.seriesKey === targetFrame.seriesKey && m.timeframeTransitionFrame.windowSecs > targetFrame.windowSecs;
    const viewportTransitionComplete = isTimeframeViewportTransitionComplete({
      retainOutgoingData: retainOutgoingDataDuringViewportTransition,
      transitionGeneration: m.timeframeTransitionGeneration,
      completedTransitionGeneration
    });
    handleWindowTransitionComplete = completedWindowSecs => {
      if (!retainOutgoingDataDuringViewportTransition || completedWindowSecs !== targetFrame.windowSecs) {
        return;
      }
      setCompletedTransitionGeneration(m.timeframeTransitionGeneration);
    };
    renderedFrame = resolveConnectedRenderedFrame({
      targetFrame,
      targetReady,
      capturedFrame: m.timeframeTransitionFrame ?? m.renderedChartFrameStore.get(),
      viewportTransitionComplete
    });
    t1 = resolveMetricChartTransition({
      committed: m.renderedChartFrameStore.get(),
      target: targetFrame,
      targetReady,
      completedSeriesKey: completedMetricSeriesKey
    });
    $[0] = authoritative;
    $[1] = completedMetricSeriesKey;
    $[2] = completedTransitionGeneration;
    $[3] = m;
    $[4] = marketPubkey;
    $[5] = trajectories;
    $[6] = handleWindowTransitionComplete;
    $[7] = renderedFrame;
    $[8] = t1;
    $[9] = targetFrame;
    $[10] = targetReady;
  } else {
    handleWindowTransitionComplete = $[6];
    renderedFrame = $[7];
    t1 = $[8];
    targetFrame = $[9];
    targetReady = $[10];
  }
  const metricTransition = t1;
  let t2;
  if ($[11] !== authoritative?.ready || $[12] !== m.interval || $[13] !== m.renderedChartFrameStore || $[14] !== metricTransition || $[15] !== renderedFrame || $[16] !== targetFrame) {
    t2 = () => {
      if (renderedFrame === null || !metricTransition.commitTarget) {
        return;
      }
      const policy = resolveConnectedChartFramePolicy({
        interval: m.interval,
        requestReady: true,
        authoritativeReady: authoritative?.ready ?? false,
        data: renderedFrame.data
      });
      if (policy.remember) {
        rememberCommittedTimeframeRenderedFrame(m.renderedChartFrameStore, renderedFrame, targetFrame);
      }
    };
    $[11] = authoritative?.ready;
    $[12] = m.interval;
    $[13] = m.renderedChartFrameStore;
    $[14] = metricTransition;
    $[15] = renderedFrame;
    $[16] = targetFrame;
    $[17] = t2;
  } else {
    t2 = $[17];
  }
  useLayoutEffect(t2);
  const targetDisplayFrame = renderedFrame ?? targetFrame;
  const chartLoading = renderedFrame === null && metricTransition.source === null;
  const seriesOpacity = !targetReady && renderedFrame !== null ? TIMEFRAME_TARGET_LOADING_SERIES_OPACITY : 1;
  const transitionDuration = reduceMotion ? 0 : METRIC_CROSSFADE_SECONDS;
  let t3;
  if ($[18] !== chartHeight || $[19] !== chartLoading || $[20] !== handleWindowTransitionComplete || $[21] !== m || $[22] !== marketPubkey || $[23] !== metricTransition || $[24] !== mobileChartHeight || $[25] !== seriesColorVar || $[26] !== seriesOpacity || $[27] !== targetDisplayFrame || $[28] !== targetFrame || $[29] !== targetReady || $[30] !== thresholdSeries || $[31] !== transitionDuration) {
    t3 = <ConnectedMarketChartTransitionView model={m} marketPubkey={marketPubkey} seriesColorVar={seriesColorVar} chartHeight={chartHeight} mobileChartHeight={mobileChartHeight} metricTransition={metricTransition} targetFrame={targetFrame} targetDisplayFrame={targetDisplayFrame} targetReady={targetReady} chartLoading={chartLoading} seriesOpacity={seriesOpacity} transitionDuration={transitionDuration} handleWindowTransitionComplete={handleWindowTransitionComplete} setCompletedMetricSeriesKey={setCompletedMetricSeriesKey} thresholdSeries={thresholdSeries} />;
    $[18] = chartHeight;
    $[19] = chartLoading;
    $[20] = handleWindowTransitionComplete;
    $[21] = m;
    $[22] = marketPubkey;
    $[23] = metricTransition;
    $[24] = mobileChartHeight;
    $[25] = seriesColorVar;
    $[26] = seriesOpacity;
    $[27] = targetDisplayFrame;
    $[28] = targetFrame;
    $[29] = targetReady;
    $[30] = thresholdSeries;
    $[31] = transitionDuration;
    $[32] = t3;
  } else {
    t3 = $[32];
  }
  return t3;
}
function ConnectedMarketChartTransitionView(t0) {
  const $ = _c(31);
  const {
    model: m,
    marketPubkey,
    seriesColorVar,
    chartHeight,
    mobileChartHeight,
    metricTransition,
    targetFrame,
    targetDisplayFrame,
    targetReady,
    chartLoading,
    seriesOpacity,
    transitionDuration,
    handleWindowTransitionComplete,
    setCompletedMetricSeriesKey,
    thresholdSeries
  } = t0;
  let t1;
  if ($[0] !== chartHeight || $[1] !== m || $[2] !== marketPubkey || $[3] !== metricTransition.source || $[4] !== mobileChartHeight || $[5] !== seriesColorVar || $[6] !== targetReady || $[7] !== thresholdSeries || $[8] !== transitionDuration) {
    t1 = metricTransition.source === null ? null : <motionElement.div key={metricTransition.source.seriesKey} aria-hidden={true} data-testid="metric-transition-source" className="pointer-events-none col-start-1 row-start-1 min-w-0" initial={false} animate={{
      opacity: targetReady ? 0 : 1
    }} transition={{
      duration: transitionDuration,
      ease: "easeOut"
    }}><ConnectedMarketChartSurface frame={metricTransition.source} model={m} marketPubkey={marketPubkey} seriesColorVar={seriesColorVar} chartHeight={chartHeight} mobileChartHeight={mobileChartHeight} loading={false} seriesOpacity={1} thresholdSeries={thresholdSeries} /></motionElement.div>;
    $[0] = chartHeight;
    $[1] = m;
    $[2] = marketPubkey;
    $[3] = metricTransition.source;
    $[4] = mobileChartHeight;
    $[5] = seriesColorVar;
    $[6] = targetReady;
    $[7] = thresholdSeries;
    $[8] = transitionDuration;
    $[9] = t1;
  } else {
    t1 = $[9];
  }
  let t2;
  if ($[10] !== chartHeight || $[11] !== chartLoading || $[12] !== handleWindowTransitionComplete || $[13] !== m || $[14] !== marketPubkey || $[15] !== metricTransition.showTarget || $[16] !== metricTransition.source || $[17] !== mobileChartHeight || $[18] !== seriesColorVar || $[19] !== seriesOpacity || $[20] !== setCompletedMetricSeriesKey || $[21] !== targetDisplayFrame || $[22] !== targetFrame || $[23] !== targetReady || $[24] !== thresholdSeries || $[25] !== transitionDuration) {
    t2 = metricTransition.showTarget ? <motionElement.div key={targetDisplayFrame.seriesKey} data-testid="metric-transition-target" className="col-start-1 row-start-1 min-w-0" initial={metricTransition.source === null ? false : {
      opacity: 0
    }} animate={{
      opacity: 1
    }} transition={{
      duration: transitionDuration,
      ease: "easeOut"
    }} {...metricTransition.source === null || !targetReady ? {} : {
      onAnimationComplete: () => setCompletedMetricSeriesKey(targetFrame.seriesKey)
    }}><ConnectedMarketChartSurface frame={targetDisplayFrame} model={m} marketPubkey={marketPubkey} seriesColorVar={seriesColorVar} chartHeight={chartHeight} mobileChartHeight={mobileChartHeight} loading={chartLoading} seriesOpacity={seriesOpacity} thresholdSeries={thresholdSeries} tradeTape={m.tradeTape} onWindowTransitionComplete={handleWindowTransitionComplete} /></motionElement.div> : null;
    $[10] = chartHeight;
    $[11] = chartLoading;
    $[12] = handleWindowTransitionComplete;
    $[13] = m;
    $[14] = marketPubkey;
    $[15] = metricTransition.showTarget;
    $[16] = metricTransition.source;
    $[17] = mobileChartHeight;
    $[18] = seriesColorVar;
    $[19] = seriesOpacity;
    $[20] = setCompletedMetricSeriesKey;
    $[21] = targetDisplayFrame;
    $[22] = targetFrame;
    $[23] = targetReady;
    $[24] = thresholdSeries;
    $[25] = transitionDuration;
    $[26] = t2;
  } else {
    t2 = $[26];
  }
  let t3;
  if ($[27] !== chartLoading || $[28] !== t1 || $[29] !== t2) {
    t3 = <LazyMotion features={domAnimation} strict={true}><div data-chart-frame={true} aria-busy={chartLoading} className="grid min-w-0 grid-cols-[minmax(0,1fr)]">{t1}{t2}</div></LazyMotion>;
    $[27] = chartLoading;
    $[28] = t1;
    $[29] = t2;
    $[30] = t3;
  } else {
    t3 = $[30];
  }
  return t3;
}
interface ConnectedMarketChartSurfaceProps extends ConnectedMarketChartProps {
  readonly frame: ConnectedChartRenderedFrame;
  readonly loading: boolean;
  readonly seriesOpacity: number;
  readonly thresholdSeries?: ReadonlyArray<LivelinePoint> | undefined;
  readonly tradeTape?: ReadonlyArray<MarketChartTradeTapeEvent>;
  readonly onWindowTransitionComplete?: (windowSecs: number) => void;
}
function ConnectedMarketChartSurface(t0) {
  const $ = _c(74);
  const {
    frame,
    model: m,
    marketPubkey,
    seriesColorVar,
    chartHeight,
    mobileChartHeight,
    loading,
    seriesOpacity,
    thresholdSeries,
    tradeTape,
    onWindowTransitionComplete
  } = t0;
  let t1;
  if ($[0] !== marketPubkey) {
    t1 = state => state.outcomeFilters[marketPubkey];
    $[0] = marketPubkey;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const outcomeFilters = useChartSettingsStore(t1);
  const toggleOutcomeFilter = useChartSettingsStore(_temp26);
  const initialTime = !m.locked && m.interval === "all" && m.marketChartMode === "line" ? Object.values(frame.data).find(_temp27)?.initialPoint?.time : undefined;
  let t2;
  if ($[2] !== m.outcomes) {
    t2 = m.outcomes ?? [];
    $[2] = m.outcomes;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  const t3 = frame.data;
  let t4;
  if ($[4] !== frame.liveValues) {
    t4 = frame.liveValues !== undefined ? {
      liveValues: frame.liveValues
    } : {};
    $[4] = frame.liveValues;
    $[5] = t4;
  } else {
    t4 = $[5];
  }
  let t5;
  if ($[6] !== frame.authoritativeFrameSource) {
    t5 = frame.authoritativeFrameSource === undefined ? {} : {
      frameSource: frame.authoritativeFrameSource
    };
    $[6] = frame.authoritativeFrameSource;
    $[7] = t5;
  } else {
    t5 = $[7];
  }
  const t6 = m.showBreakEven;
  const t7 = m.breakevens;
  const t8 = m.v2Trades;
  const t9 = m.v2Boosts;
  let t10;
  if ($[8] !== tradeTape) {
    t10 = tradeTape === undefined ? {} : {
      tradeTape
    };
    $[8] = tradeTape;
    $[9] = t10;
  } else {
    t10 = $[9];
  }
  let t11;
  if ($[10] !== m.v2Threshold) {
    t11 = m.v2Threshold !== undefined ? {
      threshold: m.v2Threshold
    } : {};
    $[10] = m.v2Threshold;
    $[11] = t11;
  } else {
    t11 = $[11];
  }
  let t12;
  if ($[12] !== thresholdSeries) {
    t12 = thresholdSeries === undefined ? {} : {
      thresholdSeries
    };
    $[12] = thresholdSeries;
    $[13] = t12;
  } else {
    t12 = $[13];
  }
  let t13;
  if ($[14] !== m.resolutionMarkerUnix) {
    t13 = m.resolutionMarkerUnix !== undefined ? {
      resolutionTimestamp: m.resolutionMarkerUnix
    } : {};
    $[14] = m.resolutionMarkerUnix;
    $[15] = t13;
  } else {
    t13 = $[15];
  }
  let t14;
  if ($[16] !== m.chartRightEdgeSec || $[17] !== m.locked) {
    t14 = m.locked ? {
      nowOverride: m.chartRightEdgeSec
    } : {};
    $[16] = m.chartRightEdgeSec;
    $[17] = m.locked;
    $[18] = t14;
  } else {
    t14 = $[18];
  }
  const t15 = m.locked;
  let t16;
  if ($[19] !== initialTime) {
    t16 = initialTime === undefined ? {} : {
      historyStartTime: initialTime
    };
    $[19] = initialTime;
    $[20] = t16;
  } else {
    t16 = $[20];
  }
  const t17 = !m.locked;
  const t18 = frame.chartMode;
  const t19 = frame.yAxisProperty;
  let t20;
  if ($[21] !== frame.seriesKey || $[22] !== frame.windowSecs || $[23] !== m.locked) {
    t20 = resolveTerminalChartSnapKey({
      locked: m.locked,
      seriesKey: frame.seriesKey,
      windowSecs: frame.windowSecs
    });
    $[21] = frame.seriesKey;
    $[22] = frame.windowSecs;
    $[23] = m.locked;
    $[24] = t20;
  } else {
    t20 = $[24];
  }
  const t21 = outcomeFilters ?? EMPTY_INDICES;
  const t22 = frame.data[m.focusedOutcomeIndex]?.bucketSeconds ?? m.candleWidthSeconds;
  let t23;
  if ($[25] !== frame.dataWindowSecs) {
    t23 = frame.dataWindowSecs === undefined ? {} : {
      dataWindowSecs: frame.dataWindowSecs
    };
    $[25] = frame.dataWindowSecs;
    $[26] = t23;
  } else {
    t23 = $[26];
  }
  let t24;
  if ($[27] !== onWindowTransitionComplete) {
    t24 = onWindowTransitionComplete === undefined ? {} : {
      onWindowTransitionComplete
    };
    $[27] = onWindowTransitionComplete;
    $[28] = t24;
  } else {
    t24 = $[28];
  }
  let t25;
  if ($[29] !== seriesColorVar) {
    t25 = seriesColorVar !== undefined ? {
      seriesColorVar
    } : {};
    $[29] = seriesColorVar;
    $[30] = t25;
  } else {
    t25 = $[30];
  }
  let t26;
  if ($[31] !== m || $[32] !== marketPubkey || $[33] !== toggleOutcomeFilter) {
    t26 = index => selectMarketDetailOutcome({
      marketPubkey,
      index,
      outcomes: m.outcomes?.map(_temp28),
      setSelectedOutcome: (market, index_0) => {
        if (m.marketChartMode === "line") {
          toggleOutcomeFilter(market, index_0, m.outcomes?.map(_temp29) ?? []);
        } else {
          m.setSelectedOutcome(market, index_0);
        }
      }
    });
    $[31] = m;
    $[32] = marketPubkey;
    $[33] = toggleOutcomeFilter;
    $[34] = t26;
  } else {
    t26 = $[34];
  }
  const t27 = m.marketChartMode === "line";
  const t28 = m.marketChartMode === "line";
  let t29;
  if ($[35] !== chartHeight || $[36] !== frame.chartMode || $[37] !== frame.data || $[38] !== frame.lineCurve || $[39] !== frame.windowSecs || $[40] !== frame.yAxisProperty || $[41] !== loading || $[42] !== m.breakevens || $[43] !== m.candleNowSec || $[44] !== m.focusedOutcomeIndex || $[45] !== m.hiddenOutcomes || $[46] !== m.locked || $[47] !== m.marketChartMode || $[48] !== m.onPlotWidthChange || $[49] !== m.showBreakEven || $[50] !== m.v2Boosts || $[51] !== m.v2Trades || $[52] !== mobileChartHeight || $[53] !== seriesOpacity || $[54] !== t10 || $[55] !== t11 || $[56] !== t12 || $[57] !== t13 || $[58] !== t14 || $[59] !== t16 || $[60] !== t17 || $[61] !== t2 || $[62] !== t20 || $[63] !== t21 || $[64] !== t22 || $[65] !== t23 || $[66] !== t24 || $[67] !== t25 || $[68] !== t26 || $[69] !== t27 || $[70] !== t28 || $[71] !== t4 || $[72] !== t5) {
    t29 = <MarketChart outcomes={t2} chartData={t3} loading={loading} seriesOpacity={seriesOpacity} {...t4} {...t5} showBreakEven={t6} breakevens={t7} trades={t8} boostEvents={t9} {...t10} {...t11} {...t12} {...t13} {...t14} locked={t15} {...t16} pulse={t17} chartModeOverride={t18} yAxisPropertyOverride={t19} snapKey={t20} mode={m.marketChartMode} hiddenOutcomeIndices={m.hiddenOutcomes} selectedOutcomeIndices={t21} focusedOutcomeIndex={m.focusedOutcomeIndex} candleWidthSeconds={t22} candleNowSeconds={m.candleNowSec} height={chartHeight} mobileHeight={mobileChartHeight} windowSecs={frame.windowSecs} {...t23} {...t24} onPlotWidthChange={m.onPlotWidthChange} lineCurve={frame.lineCurve} {...t25} onSelectOutcome={t26} extendLeftEdge={t27} holdLastValueToRightEdge={t28} dotsBackground={true} degen={MARKET_CHART_DEGEN_OPTIONS} />;
    $[35] = chartHeight;
    $[36] = frame.chartMode;
    $[37] = frame.data;
    $[38] = frame.lineCurve;
    $[39] = frame.windowSecs;
    $[40] = frame.yAxisProperty;
    $[41] = loading;
    $[42] = m.breakevens;
    $[43] = m.candleNowSec;
    $[44] = m.focusedOutcomeIndex;
    $[45] = m.hiddenOutcomes;
    $[46] = m.locked;
    $[47] = m.marketChartMode;
    $[48] = m.onPlotWidthChange;
    $[49] = m.showBreakEven;
    $[50] = m.v2Boosts;
    $[51] = m.v2Trades;
    $[52] = mobileChartHeight;
    $[53] = seriesOpacity;
    $[54] = t10;
    $[55] = t11;
    $[56] = t12;
    $[57] = t13;
    $[58] = t14;
    $[59] = t16;
    $[60] = t17;
    $[61] = t2;
    $[62] = t20;
    $[63] = t21;
    $[64] = t22;
    $[65] = t23;
    $[66] = t24;
    $[67] = t25;
    $[68] = t26;
    $[69] = t27;
    $[70] = t28;
    $[71] = t4;
    $[72] = t5;
    $[73] = t29;
  } else {
    t29 = $[73];
  }
  return t29;
}
function _temp29(outcome_0) {
  return outcome_0.index;
}
function _temp28(outcome) {
  return {
    outcomeIndex: outcome.index,
    label: outcome.title
  };
}
function _temp27(entry) {
  return entry.initialPoint !== undefined;
}
function _temp26(state_0) {
  return state_0.toggleOutcomeFilter;
}
export function ConnectedChartContainer(props) {
  const $ = _c(13);
  let t0;
  if ($[0] !== props.marketPubkey) {
    t0 = {
      pubkey: props.marketPubkey
    };
    $[0] = props.marketPubkey;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  const marketQuery = useGetMarket(t0);
  const snapshot = useChartMarketSnapshot(marketQuery.data?.pubkey === props.marketPubkey ? marketQuery.data : undefined);
  const holding = props.resolutionHolding === true;
  let t1;
  if ($[2] !== holding || $[3] !== snapshot) {
    t1 = holding && snapshot !== undefined ? {
      ...snapshot,
      status: "trading" as const
    } : snapshot;
    $[2] = holding;
    $[3] = snapshot;
    $[4] = t1;
  } else {
    t1 = $[4];
  }
  const currentMarketData = useHeldWhile(t1, holding);
  const deferredMarketData = useDeferredValue(currentMarketData);
  if (currentMarketData === undefined) {
    return null;
  }
  const renderedMarketData = deferredMarketData?.pubkey === props.marketPubkey ? deferredMarketData : currentMarketData;
  const t2 = props.lifecycleStatus ?? currentMarketData.status;
  let t3;
  if ($[5] !== currentMarketData.createdAt || $[6] !== currentMarketData.resolvedAt || $[7] !== currentMarketData.tradingStartedAt || $[8] !== holding || $[9] !== props || $[10] !== renderedMarketData || $[11] !== t2) {
    t3 = <ConnectedChartContainerView {...props} marketData={renderedMarketData} lifecycleStatus={t2} lifecycleCreatedAt={currentMarketData.createdAt} lifecycleTradingStartedAt={currentMarketData.tradingStartedAt} lifecycleResolvedAt={currentMarketData.resolvedAt} resolutionLanded={holding} />;
    $[5] = currentMarketData.createdAt;
    $[6] = currentMarketData.resolvedAt;
    $[7] = currentMarketData.tradingStartedAt;
    $[8] = holding;
    $[9] = props;
    $[10] = renderedMarketData;
    $[11] = t2;
    $[12] = t3;
  } else {
    t3 = $[12];
  }
  return t3;
}
function RetainedHistoricalQueries(t0) {
  const $ = _c(50);
  const {
    m,
    marketPubkey
  } = t0;
  const {
    backendProperty,
    backendChartType,
    backendInterval
  } = m;
  const terminalOpening = m.terminalOpening;
  const historyFamily = backendProperty === "twar" ? "twar" : "execution";
  const t1 = m.liveWindowSeconds ?? m.chartFrom ?? "";
  const t2 = m.liveWindowSeconds === undefined ? m.chartTo ?? "" : "rolling";
  let t3;
  if ($[0] !== backendChartType || $[1] !== backendInterval || $[2] !== historyFamily || $[3] !== m.targetBars || $[4] !== t1 || $[5] !== t2) {
    t3 = [backendInterval, backendChartType, historyFamily, m.targetBars, t1, t2];
    $[0] = backendChartType;
    $[1] = backendInterval;
    $[2] = historyFamily;
    $[3] = m.targetBars;
    $[4] = t1;
    $[5] = t2;
    $[6] = t3;
  } else {
    t3 = $[6];
  }
  const historyKey = t3.join("|");
  let t4;
  if ($[7] !== m.renderedOutcomeIndices) {
    t4 = m.renderedOutcomeIndices.join(",");
    $[7] = m.renderedOutcomeIndices;
    $[8] = t4;
  } else {
    t4 = $[8];
  }
  let t5;
  if ($[9] !== backendChartType || $[10] !== backendInterval || $[11] !== backendProperty || $[12] !== historyKey || $[13] !== m.chartDataRequestKey || $[14] !== m.chartFrom || $[15] !== m.chartTo || $[16] !== m.interval || $[17] !== m.liveWindowSeconds || $[18] !== m.targetBars) {
    t5 = m.interval === "live" || backendProperty === "twar" ? null : {
      key: historyKey,
      requestKey: m.chartDataRequestKey,
      interval: backendInterval,
      property: backendProperty,
      chartType: backendChartType,
      targetBars: m.targetBars,
      ...(m.liveWindowSeconds === undefined ? {
        ...(m.chartFrom === undefined ? {} : {
          from: m.chartFrom
        }),
        ...(m.chartTo === undefined ? {} : {
          to: m.chartTo
        })
      } : {
        liveWindowSeconds: m.liveWindowSeconds
      })
    };
    $[9] = backendChartType;
    $[10] = backendInterval;
    $[11] = backendProperty;
    $[12] = historyKey;
    $[13] = m.chartDataRequestKey;
    $[14] = m.chartFrom;
    $[15] = m.chartTo;
    $[16] = m.interval;
    $[17] = m.liveWindowSeconds;
    $[18] = m.targetBars;
    $[19] = t5;
  } else {
    t5 = $[19];
  }
  const retainedHistory = useRetainedChartHistory(`${marketPubkey}|${m.locked ? "terminal" : "open"}|${t4}`, t5, m.presentationHistoryRequestKey);
  let t6;
  if ($[20] !== backendProperty || $[21] !== historyKey || $[22] !== m.interval || $[23] !== m.presentationHistoryRequestKey || $[24] !== m.renderedOutcomeIndices || $[25] !== m.setChartLeafData || $[26] !== m.setNowMinuteMs || $[27] !== m.setRetainedChartLeafData || $[28] !== marketPubkey || $[29] !== retainedHistory) {
    let t7;
    if ($[31] !== backendProperty || $[32] !== historyKey || $[33] !== m.interval || $[34] !== m.presentationHistoryRequestKey || $[35] !== m.renderedOutcomeIndices || $[36] !== m.setChartLeafData || $[37] !== m.setNowMinuteMs || $[38] !== m.setRetainedChartLeafData || $[39] !== marketPubkey) {
      t7 = history => history.properties.flatMap(property => m.renderedOutcomeIndices.map(i => <HistoricalChartQueryOwner key={`historical-owner-${marketPubkey}-${i}-${property}-${history.key}`} marketPubkey={marketPubkey} outcomeIndex={i} interval={history.interval} property={property} chartType={history.chartType} {...history.targetBars === undefined ? {} : {
        targetBars: history.targetBars
      }} {...history.from === undefined ? {} : {
        from: history.from
      }} {...history.to === undefined ? {} : {
        to: history.to
      }} {...history.liveWindowSeconds === undefined ? {} : {
        liveWindowSeconds: history.liveWindowSeconds
      }} {...m.interval !== "live" && history.requestKey !== undefined && (history.key === historyKey || history.requestKey === m.presentationHistoryRequestKey) && property === backendProperty ? {
        publisher: {
          requestKey: history.requestKey,
          ...(m.interval === "all" ? {
            setAnchorMinuteMs: m.setNowMinuteMs
          } : {}),
          retainDataOnUnmount: true,
          setData: history.key === historyKey ? m.setChartLeafData : m.setRetainedChartLeafData
        }
      } : {}} />));
      $[31] = backendProperty;
      $[32] = historyKey;
      $[33] = m.interval;
      $[34] = m.presentationHistoryRequestKey;
      $[35] = m.renderedOutcomeIndices;
      $[36] = m.setChartLeafData;
      $[37] = m.setNowMinuteMs;
      $[38] = m.setRetainedChartLeafData;
      $[39] = marketPubkey;
      $[40] = t7;
    } else {
      t7 = $[40];
    }
    t6 = retainedHistory.flatMap(t7);
    $[20] = backendProperty;
    $[21] = historyKey;
    $[22] = m.interval;
    $[23] = m.presentationHistoryRequestKey;
    $[24] = m.renderedOutcomeIndices;
    $[25] = m.setChartLeafData;
    $[26] = m.setNowMinuteMs;
    $[27] = m.setRetainedChartLeafData;
    $[28] = marketPubkey;
    $[29] = retainedHistory;
    $[30] = t6;
  } else {
    t6 = $[30];
  }
  let t7;
  if ($[41] !== backendProperty || $[42] !== m.renderedOutcomeIndices || $[43] !== m.setTerminalOpeningData || $[44] !== marketPubkey || $[45] !== terminalOpening) {
    t7 = terminalOpening === undefined || backendProperty === "twar" ? null : m.renderedOutcomeIndices.map(i_0 => <HistoricalChartQueryOwner key={`${marketPubkey}-${i_0}-terminal-opening-${terminalOpening.requestKey}`} marketPubkey={marketPubkey} outcomeIndex={i_0} interval="1m" property={backendProperty} chartType="line" from={terminalOpening.from} to={terminalOpening.to} publisher={{
      requestKey: terminalOpening.requestKey,
      setData: m.setTerminalOpeningData
    }} />);
    $[41] = backendProperty;
    $[42] = m.renderedOutcomeIndices;
    $[43] = m.setTerminalOpeningData;
    $[44] = marketPubkey;
    $[45] = terminalOpening;
    $[46] = t7;
  } else {
    t7 = $[46];
  }
  let t8;
  if ($[47] !== t6 || $[48] !== t7) {
    t8 = <>{t6}{t7}</>;
    $[47] = t6;
    $[48] = t7;
    $[49] = t8;
  } else {
    t8 = $[49];
  }
  return t8;
}
function RetainedLiveSeeds(t0) {
  const $ = _c(9);
  const {
    m,
    marketPubkey
  } = t0;
  const retainedSeeds = useRetainedChartSeeds(`${marketPubkey}|${m.locked}`, false, m.marketCapLiveEnabled);
  let t1;
  if ($[0] !== m.locked || $[1] !== m.marketCapLiveEnabled || $[2] !== m.renderedOutcomeIndices || $[3] !== m.setMarketCapSeedData || $[4] !== marketPubkey || $[5] !== retainedSeeds.marketCap) {
    t1 = retainedSeeds.marketCap && !m.locked ? m.renderedOutcomeIndices.map(i => <HistoricalChartQueryOwner key={`${i}-market-cap-seed-${marketPubkey}`} marketPubkey={marketPubkey} outcomeIndex={i} interval="1m" property="market_cap" chartType="line" liveWindowSeconds={String(LIVE_CHART_SEED_SECONDS)} {...m.marketCapLiveEnabled ? {
      publisher: {
        setData: m.setMarketCapSeedData,
        retainDataOnUnmount: true
      }
    } : {}} />) : null;
    $[0] = m.locked;
    $[1] = m.marketCapLiveEnabled;
    $[2] = m.renderedOutcomeIndices;
    $[3] = m.setMarketCapSeedData;
    $[4] = marketPubkey;
    $[5] = retainedSeeds.marketCap;
    $[6] = t1;
  } else {
    t1 = $[6];
  }
  let t2;
  if ($[7] !== t1) {
    t2 = <>{t1}</>;
    $[7] = t1;
    $[8] = t2;
  } else {
    t2 = $[8];
  }
  return t2;
}
function ConnectedChartContainerView(props) {
  const $ = _c(24);
  const hydrated = useHydrated();
  const {
    marketPubkey,
    className,
    seriesColorVar,
    readOnly: t0,
    chartHeight: t1,
    mobileChartHeight: t2
  } = props;
  const readOnly = t0 === undefined ? false : t0;
  const chartHeight = t1 === undefined ? CHART_CANVAS_HEIGHT_DESKTOP : t1;
  const mobileChartHeight = t2 === undefined ? CHART_CANVAS_HEIGHT_MOBILE : t2;
  const m = useConnectedChart(props);
  let t3;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = {
      width: "100%"
    };
    $[0] = t3;
  } else {
    t3 = $[0];
  }
  const t4 = m.chartMode === "consensus" ? "market_share" : m.yAxisProperty === "marketCap" ? "market_cap" : "price";
  let t5;
  if ($[1] !== hydrated || $[2] !== m || $[3] !== marketPubkey) {
    t5 = hydrated ? <><RetainedHistoricalQueries m={m} marketPubkey={marketPubkey} /><RetainedLiveSeeds m={m} marketPubkey={marketPubkey} /></> : null;
    $[1] = hydrated;
    $[2] = m;
    $[3] = marketPubkey;
    $[4] = t5;
  } else {
    t5 = $[4];
  }
  let t6;
  if ($[5] !== chartHeight || $[6] !== m || $[7] !== marketPubkey || $[8] !== mobileChartHeight || $[9] !== props.marketData || $[10] !== props.resolutionLanded || $[11] !== seriesColorVar) {
    t6 = <div className="mb-4"><ConnectedMarketChart model={m} marketData={props.marketData} resolutionLanded={props.resolutionLanded} marketPubkey={marketPubkey} seriesColorVar={seriesColorVar} chartHeight={chartHeight} mobileChartHeight={mobileChartHeight} /></div>;
    $[5] = chartHeight;
    $[6] = m;
    $[7] = marketPubkey;
    $[8] = mobileChartHeight;
    $[9] = props.marketData;
    $[10] = props.resolutionLanded;
    $[11] = seriesColorVar;
    $[12] = t6;
  } else {
    t6 = $[12];
  }
  let t7;
  if ($[13] !== m || $[14] !== marketPubkey || $[15] !== readOnly) {
    t7 = !readOnly && <TooltipProvider delayDuration={300}><ChartControls marketPubkey={marketPubkey} outcomes={m.v2OutcomesWithColor} availableIntervals={m.dataAvailableIntervals ?? DEFAULT_AVAILABLE_INTERVALS} locked={m.locked} chartMode={m.chartMode} interval={m.interval} yAxisProperty={m.yAxisProperty} onIntervalChange={m.setChartInterval} onYAxisPropertyChange={m.setYAxisProperty} onChartModeChange={mode => {
        m.setChartMode(mode);
      }} chartToolbarExtras={<ChartSettingsMenu />} /></TooltipProvider>;
    $[13] = m;
    $[14] = marketPubkey;
    $[15] = readOnly;
    $[16] = t7;
  } else {
    t7 = $[16];
  }
  let t8;
  if ($[17] !== className || $[18] !== marketPubkey || $[19] !== t4 || $[20] !== t5 || $[21] !== t6 || $[22] !== t7) {
    t8 = <div className={className} style={t3} data-trade-chart-market={marketPubkey} data-trade-chart-metric={t4}>{t5}{t6}{t7}</div>;
    $[17] = className;
    $[18] = marketPubkey;
    $[19] = t4;
    $[20] = t5;
    $[21] = t6;
    $[22] = t7;
    $[23] = t8;
  } else {
    t8 = $[23];
  }
  return t8;
}